<template>
    <div>
        <!-- Hidden Scanner Input for DataWedge (TC200 & TC22) -->
        <input
            ref="scannerInput"
            type="text"
            inputmode="none"
            :class="scannerOptions.debug ? 'scanner-input-visible' : 'scanner-input-hidden'"
            @input="handleScannerInput"
            @keydown="handleScannerKeydown"
            @focus="handleScannerFocus"
            @blur="handleScannerBlur"
            autocomplete="off"
        />

        <!-- Loading State -->
        <LoadingState v-if="isLoading" />

        <!-- Empty State -->
        <EmptyState v-else-if="!currentOrder" :errorType="errorState.type" :errorMessage="errorState.message"
            :orderId="errorState.orderId" @retry="getNextOrder" />

        <!-- Main Wizard -->
        <div v-else class="multicart-wizard">
            <!-- Development Mode Badge -->
            <div v-if="isDevelopmentMode" class="dev-mode-badge">
                <v-chip color="warning" dark x-small>
                    <v-icon left x-small>mdi-code-tags</v-icon>
                    DEV
                </v-chip>
            </div>

            <!-- Dev Terminal (Desktop Only) -->
            <div v-if="isDevelopmentMode" class="dev-terminal">
                <div class="terminal-header">
                    <div class="terminal-title">
                        <v-icon small color="#00ff41" class="mr-2">mdi-console</v-icon>
                        <span>MULTICART_DEV</span>
                        <span class="terminal-blink ml-2">_</span>
                    </div>
                    <div class="terminal-status">
                        <span class="status-dot"></span>
                        <span>ONLINE</span>
                    </div>
                </div>

                <div class="terminal-body">
                    <div class="terminal-content">
                        <!-- Workflow State -->
                        <div class="terminal-section">
                            <div class="terminal-prompt">&gt; WORKFLOW_STATE</div>
                            <pre class="terminal-data">{{
                                {
                                    scannedItems: scannedItems + '/' + itemsToScan,
                                    isPrinted: isPrinted,
                                    trackingScanned: allTrackingIdsScanned,
                                    readyToPack: isPrinted && allTrackingIdsScanned
                                }
                            }}</pre>
                        </div>

                        <!-- Tracking ID Display -->
                        <div v-if="isPrinted" class="terminal-section">
                            <div class="terminal-prompt">&gt; TRACKING_ID</div>
                            <div v-if="hasAdditionalTrackingIds" class="terminal-data">
                                <div v-for="(trackingId, index) in requiredTrackingIds" :key="index"
                                    style="margin-bottom: 4px;">
                                    <span
                                        :style="{ color: scannedTrackingIds.includes(trackingId) ? '#00ff41' : '#666' }">
                                        {{ scannedTrackingIds.includes(trackingId) ? '✓' : '○' }} {{
                                        trackingId.substring(0, 12) }}...
                                    </span>
                                </div>
                            </div>
                            <div v-else-if="currentOrder.tracking" class="terminal-data">
                                <span :style="{ color: trackingIdScanned ? '#00ff41' : '#666' }">
                                    {{ trackingIdScanned ? '✓' : '○' }} {{ currentOrder.tracking.substring(0, 12) }}...
                                </span>
                            </div>
                            <div v-else class="terminal-data" style="color: #666;">
                                Waiting for tracking ID...
                            </div>
                        </div>

                        <!-- Article List -->
                        <div class="terminal-section" v-if="currentProducts && currentProducts.length > 0">
                            <div class="terminal-prompt">&gt; ARTICLES ({{ currentProducts.length }})</div>
                            <div class="terminal-hint">Click to toggle scanned state</div>
                            <div class="terminal-article-list">
                                <div v-for="(article, index) in currentProducts" :key="index"
                                    class="terminal-article-item"
                                    :class="{ 'scanned': (article.scannedCount || 0) >= (article.qty || 1) }"
                                    @click="devToggleArticleScanned(article)">
                                    <div class="article-sku">{{ article.sku }}</div>
                                    <div class="article-qty">{{ article.scannedCount || 0 }}/{{ article.qty || 1 }}
                                    </div>
                                    <v-icon small
                                        :color="(article.scannedCount || 0) >= (article.qty || 1) ? '#00ff41' : '#666'">
                                        {{ (article.scannedCount || 0) >= (article.qty || 1) ? 'mdi-check-circle' :
                                        'mdi-circle-outline' }}
                                    </v-icon>
                                </div>
                            </div>
                        </div>

                        <!-- Quick Actions -->
                        <div class="terminal-actions">
                            <button class="terminal-btn terminal-btn-success" @click="devScanAllArticles"
                                :disabled="scannedItems >= itemsToScan">
                                <v-icon small class="mr-1">mdi-check-all</v-icon>
                                SCAN_ALL
                            </button>
                            <button class="terminal-btn terminal-btn-secondary" @click="resetScanProgress">
                                <v-icon small class="mr-1">mdi-refresh</v-icon>
                                RESET_SCANS
                            </button>
                            <button v-if="isPrinted && currentOrder.tracking" class="terminal-btn terminal-btn-primary"
                                @click="devSimulateTrackingScan" :disabled="allTrackingIdsScanned">
                                <v-icon small class="mr-1">mdi-barcode-scan</v-icon>
                                SCAN_TRACKING
                            </button>
                            <button class="terminal-btn terminal-btn-success" @click="pack"
                                >
                                <v-icon small class="mr-1">mdi-check-all</v-icon>
                                PACK_ORDER
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Mobile Workflow View -->
            <MobileWorkflowView ref="mobileWorkflow" v-if="isMobile" :step="mobileWorkflowStep" :articles="currentProducts"
                :swissDocumentUrls="swissDocumentUrls" :shippingLabelUrls="shippingLabelUrls"
                :currentSlot="currentSlot" :progress="mobilePickingProgress" :packingProgress="mobilePackingProgress"
                :pickingComplete="mobilePickingComplete" :packingComplete="mobilePackingComplete"
                :tracking="currentOrder.tracking" :trackingScanned="allTrackingIdsScanned"
                :scannedTrackingIds="scannedTrackingIds"
                :remainingTrackingIds="remainingTrackingIds" :pickingScannedArticles="pickingScannedArticles"
                :packingScannedArticles="packingScannedArticles" :orderId="currentOrder.orderId"
                :currentFlyers="currentFlyer" :upsellItems="upsellItems" :packaging="currentOrder?.packaging || null"
                @advance-step="advanceMobileWorkflowStep" @confirm-carton="confirmCarton"
                @confirm-upsells="confirmUpsells" @confirm-flyer="confirmFlyer" @pack="pack" @print="print" @go-back="goBack"
                @reprint-swiss-documents="printSwissDocuments" @reprint-shipping-label="printShippingLabel"
                @open-settings-modal="openSettingsModal" @open-problem-modal="openProblemModal" />

            <!-- Desktop View -->
            <div v-else class="multicart-wizard-header-body-container">
                <OrderHeader :mode="'multicart'" :currentSlot="currentSlot"
                    :currentOrder="currentOrder" :user="currentUser" :currentPrinter="currentPrinter"
                    :itemsToScan="itemsToScan" :scannedItems="scannedItems" :currentPackageWeight="currentPackageWeight"
                    :isPrintable="isPrintable" :isScanning="isScanning" :isMobileWorkflowActive="isMobileWorkflowActive"
                    :mobileWorkflowStatusText="mobileWorkflowStatusText" @open-printer-modal="openSettingsModal"
                    @open-problem-modal="openProblemModal" @open-reset-dialog="openBulkResetDialog" />
                <OrderBody :articles="currentProducts" :upsellItems="upsellItems" :currentFlyer="currentFlyer"
                    :hasMissingItems="hasMissingItems" :isMulticart="isMulticart"
                    :packaging="currentOrder?.packaging || null" :mode="'multicart'"
                    :nextItemToScan="nextItemToScan" :isPartOfPacklistChecker="() => true"
                    :layout="articleLayout" :mobileWorkflowStep="mobileWorkflowStep"
                    :pickingScannedArticles="pickingScannedArticles" :packingScannedArticles="packingScannedArticles"
                    @reset-article="resetArticle"
                    @copy-to-clipboard="copyToClipboard" @open-warranty-modal="openWarrantyModal" />
            </div>
            <OrderActions v-if="!isMobile" :mode="'multicart'" :isPrintable="isPrintable" :isPrinting="isPrinting"
                :isPrinted="isPrinted" :currentPrinter="currentPrinter" :currentOrder="currentOrder"
                :currentPackstats="{ count: currentPackstats.count, time: currentPackstats.time }"
                :nextItemToScan="nextItemToScan" :isSplittable="isSplittable" :isDevelopmentMode="isDevelopmentMode"
                :tracking="currentOrder.tracking || null" :trackingIdScanned="allTrackingIdsScanned"
                :scannedTrackingId="scannedTrackingId" :hasAdditionalTrackingIds="hasAdditionalTrackingIds"
                :trackingIdsProgress="trackingIdsProgress" :remainingTrackingIds="remainingTrackingIds"
                :isSwissOrder="isSwissOrder" :swissDocumentsPrinted="swissDocumentsPrinted"
                :swissTrackingScan="swissTrackingScan" :shippingLabelPrinted="shippingLabelPrinted"
                :trackingScanPrompt="trackingScanPrompt"
                @print="print" @split="split" @pack="pack" @open-split-modal="openSplitModal"
                @reset-scan-progress="resetScanProgress" @open-settings-modal="openSettingsModal" />
        </div>

        <!-- Modals -->
        <PackagingSplitModal v-if="isSplitModalOpen" :show="isSplitModalOpen" :articles="currentProducts"
            @close="closeSplitModal" @split-complete="handleSplitComplete" @show-message="showSnackbar" />

        <!-- Scan Error Modal (Desktop/Tablet Only) -->
        <ScanErrorModal v-if="scanErrorModal.show && !isMobile" :show="scanErrorModal.show"
            :errorType="scanErrorModal.errorType" :errorContext="scanErrorModal.errorContext"
            @close="closeScanErrorModal" @continue-scanning="handleContinueScanning"
            @reset-current-item="handleResetCurrentItem" @report-problem="handleReportScanProblem" />

        <!-- Mobile Scan Error (Inline Snackbar) -->
        <v-snackbar v-if="isMobile" v-model="scanErrorModal.show" :timeout="8000" top color="error" multi-line
            class="mobile-error-snackbar">
            <div class="d-flex align-center">
                <v-icon color="white" size="32" class="mr-3">mdi-alert-circle</v-icon>
                <div style="flex: 1;">
                    <div class="font-weight-bold text-h6 mb-1">{{ getMobileErrorTitle(scanErrorModal.errorType) }}</div>
                    <div class="text-body-2">{{ getMobileErrorMessage(scanErrorModal.errorType,
                        scanErrorModal.errorContext) }}</div>
                </div>
            </div>
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="closeScanErrorModal" large>
                    OK
                </v-btn>
            </template>
        </v-snackbar>

        <!-- PrinterModal removed - printer selection now handled by SettingsModal -->

        <ProblemModal 
            v-if="isProblemModalOpen" 
            @close-modal="closeProblemModal" 
            @reportProblem="reportProblemAndRefresh" 
            :showEngravingSection="false"
        />

        <DamagedDialog v-if="!isMobile" :show="isDamagedDialogOpen" :article="selectedArticleForReset"
            :orderId="currentOrder ? currentOrder.orderId : null" :resetOnly="damageResetOnly"
            :orderProducts="currentOrder ? currentOrder.products : []" @close="closeDamagedDialog"
            @reset-confirmed="handleArticleReset" />

        <!-- Settings Modal (All Devices) -->
        <SettingsModal :show="isSettingsModalOpen" :currentPrinter="currentPrinter" :user="currentUser"
            @close-modal="closeSettingsModal" @select-printer="selectPrinter" @layout-changed="handleLayoutChanged" />

        <WarrantyModal v-if="isWarrantyModalOpen" :warrantyReason="currentOrder?.warrantyReason"
            :warrantyNote="currentOrder?.warrantyNote" :currentOrder="currentOrder" @close-modal="closeWarrantyModal" />

        <!-- Snackbar for notifications -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" top right>
            {{ snackbar.message }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snackbar.show = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
//Components
import OrderHeader from '../shared/OrderHeader.vue';
import OrderBody from '../shared/OrderBody.vue';
import OrderActions from '../shared/OrderActions.vue';
import LoadingState from '../shared/LoadingState.vue';
import EmptyState from '../shared/EmptyState.vue';
import MobileWorkflowView from './MobileWorkflowView.vue';

//Modals
import PackagingSplitModal from './modals/PackagingSplitModal.vue';
import ScanErrorModal from '../shared/modals/ScanErrorModal.vue';
// PrinterModal removed - printer selection now handled by SettingsModal
import ProblemModal from '../shared/modals/ProblemModal.vue';
import DamagedDialog from '../shared/modals/DamagedDialog.vue';
import SettingsModal from '../shared/modals/SettingsModal.vue';
import WarrantyModal from '../shared/modals/WarrantyModal.vue';

//Services
import PacklistService from '@/services/PacklistService';
import EventBus, { PackTracker } from "@/event-bus";
import flyerMappingData from '../shared/data/flyerMapping';

// Scanner Mixin
import scannerInputMixin from '@/mixins/scannerInputMixin';
import '@/styles/scanner-input.css';

export default {
    name: 'MulticartWizard',
    mixins: [scannerInputMixin],
    components: {
        OrderHeader,
        OrderBody,
        OrderActions,
        LoadingState,
        EmptyState,
        MobileWorkflowView,

        //Modals
        PackagingSplitModal,
        ScanErrorModal,
        ProblemModal,
        DamagedDialog,
        SettingsModal,
        WarrantyModal
    },
    data() {
        return {
            isLoading: false,

            //Slot related
            currentSlot: "E7",

            //Shelf related (upper vs lower)
            currentShelf: 'lower', // Will be set from route query parameter

            //User related
            currentUser: JSON.parse(localStorage.getItem('user')),

            //Order related
            currentOrder: null,
            currentPacklistItemId: null,
            isScanning: false,
            currentScanIndex: 0,
            isFirstOrder: true, // Track if this is the first order or subsequent

            // Scanner options (from mixin)
            scannerOptions: {
                enabled: true,
                refocusDelay: 3000,
                debug: false
            },

            //Development mode
            isDevelopmentMode: false, //Initial Development Mode

            //Pack tracking
            itemViewStartTime: null, // Time when item was first loaded/viewed
            packingStarted: false,   // Whether actual packing has started
            currentPackstats: {
                progress: 0,
                targetProgress: 40,
                count: 0,
                time: 0,
                packStart: null,
                packEnd: null
            },


            //Printer
            currentPrinter: (() => {
                try {
                    const storedPrinter = localStorage.getItem('currentPrinter');
                    return storedPrinter ? JSON.parse(storedPrinter) : null;
                } catch (error) {
                    console.warn('Failed to parse stored printer data:', error);
                    return null;
                }
            })(),
            isPrinting: false,
            isPrinted: false,

            //Layout preference
            articleLayout: localStorage.getItem('articleLayout') || 'linear',

            //Tracking ID Scanning
            trackingIdScanned: false,
            additionalTrackingIds: [],
            scannedTrackingIds: [], // Array to store all scanned tracking IDs
            scannedTrackingId: null, // Keep for backward compatibility

            //Swiss & Prime Label Printing
            swissDocumentsPrinted: false,
            shippingLabelPrinted: false,
            swissTrackingScan: false,  // First scan (Swiss orders only)
            finalTrackingScan: false,  // Final scan (all orders)
            swissDocumentUrls: [],  // Store A4 document URLs for display
            shippingLabelUrls: [],  // Store shipping label URLs for display
            requiredTrackingScans: 1,  // Number of times tracking must be scanned (1 for normal, 2 for Swiss)

            //Modals
            isSplitModalOpen: false,
            isProblemModalOpen: false,
            isDamagedDialogOpen: false,
            isSettingsModalOpen: false,
            isWarrantyModalOpen: false,
            selectedArticleForReset: null,
            damageResetOnly: true,
            scanErrorModal: {
                show: false,
                errorType: '',
                errorContext: {}
            },
            snackbar: {
                show: false,
                message: '',
                color: 'success',
                timeout: 3000
            },
            //Product filtering and mappings (same as PacklistRoot)
            upsellWhitelist: [
                "LYTO_TYPE_C_KABEL",
                "LYTO_NETZSTECKER",
                "LYTO_LED_SOCKEL_WHITE",
                "wooden_led_base_HV",
                "wooden_led_base_RGB",
                "NETZSTECKER",
                "TYP_C_USB_KABEL",
                "gravurfarbe_kinderbesteck",
                "PRIO_ORDER",
                "LYTO_SAMTBEUTEL_SCHWARZ",
                "LS24_SAMTBEUTEL_RED",
                "LS24_SAMTBEUTEL_SILVER",
                "LS24_SAMTBEUTEL_BLACK",
                "Samtbeutel (schwarz)",
                "Samtbeutel (rot)",
                "GF_Organzasäckchen",
                "GF_Samtsäckchen",
                "hauck_holzleiste_5mm",
                "upsell_holzsockel5mm",
                "samtbeutel_kordelzug",
                "stoffbeutel",
                "shopify_extra_helle_gravur",
                "Taschenmesserkette",
                "fruehstuecksbrett_tiere_upsell"
            ],
            flyerMapping: flyerMappingData,
            windowWidth: window.innerWidth,
            isFullscreen: false,
            fullscreenSupported: false,

            // Error state tracking
            errorState: {
                type: 'empty', // 'empty', 'not_found', 'not_in_shelf', 'not_ready', 'server_error'
                message: '',
                orderId: null
            },

            // Mobile workflow state
            mobileWorkflowStep: 1, // 1: picking, 2: walk-to-table, 3: packing-verification, 4: flyer (if available), 5: label-scan, 6: additional-tracking, 7: complete
            pickingScannedArticles: {}, // Track which articles were scanned in picking phase { sku_hashcode: count }
            packingScannedArticles: {},  // Track which articles were scanned in packing phase { sku_hashcode: count }

            // Packlist Alerts (from Socket.io)
            currentAlert: null,
            alertDialog: false
        }
    },
    computed: {
        messageSocket() {
            return this.$store.state.messages?.socket;
        },

        isMobile() {
            // Mobile: small screen devices (phones) - width <= 480px
            return this.windowWidth <= 480;
        },

        upsellItems() {
            if (!this.currentOrder?.products) return [];

            //Filter products that are upsell=true AND !isEvent OR in upsellWhitelist
            const filteredUpsellItems = this.currentOrder.products.filter(
                product => (product.upsell === true && !product.isEvent) || this.upsellWhitelist.includes(product.sku)
            );

            //Deduplicate products
            const deduplicatedProducts = filteredUpsellItems.reduce((acc, product) => {
                const existingProduct = acc.find(p => p.sku === product.sku);

                if (existingProduct) {
                    existingProduct.qty = (existingProduct.qty || 1) + (product.qty || 1);
                } else {
                    acc.push({
                        ...product,
                        qty: product.qty || 1
                    });
                }

                return acc;
            }, []);

            console.log('upsellItems filtered:', deduplicatedProducts);
            return deduplicatedProducts;
        },
        currentProducts() {
            if (!this.currentOrder?.products) return [];

            let dontShow = ["customization"];

            const filtered = this.currentOrder.products.filter(product => {
                const isUpsell = Boolean(product.upsell) === true;
                const isEvent = Boolean(product.isEvent) === true;
                const shouldInclude = (!isUpsell && !isEvent) || (isUpsell && isEvent);
                const notInUpsellWhitelist = !this.upsellWhitelist.includes(product.sku);
                const notDontShow = !dontShow.includes(product.sku);

                return shouldInclude && notInUpsellWhitelist && notDontShow;
            });

            // Enhance products with location info for mobile display
            const enhanced = filtered.map(product => ({
                ...product,
                location: product.shelfLocation || product.location || null,
                imageUrl: product.imageUrl || product.image || null,
                designName: product.designName || product.name || null,
                hashcode: product.shelfHashcode || product.hashcode
            }));

            console.log('currentProducts filtered:', enhanced);
            return enhanced;
        },
        currentFlyer() {
            try {
                if (!this.currentOrder) {
                    console.log('currentFlyer: No currentOrder');
                    return [];
                }

                const orderId = this.currentOrder.orderId;
                const reference = this.currentOrder.reference;
                const flyers = [];

                console.log('🎯 currentFlyer DEBUG:');
                console.log('   orderId =', orderId, 'type:', typeof orderId);
                console.log('   reference =', reference);
                console.log('   flyerMapping =', this.flyerMapping);

                //Check for internal order -> check for orderId patterns
                if (!reference && orderId) {
                    console.log('   Checking orderId patterns...');
                    if (orderId.toString().startsWith('MM')) {
                        console.log('   ✅ OrderId starts with MM');
                        const value = this.flyerMapping.find(f => f.value === 'MM');
                        console.log('   🔍 Search result:', value);
                        if (value) {
                            console.log('   📋 Flyer details:', {
                                value: value.value,
                                url: value.url,
                                bg: value.bg,
                                color: value.color
                            });
                            flyers.push(value);
                        }
                    } else if (isNaN(orderId) && !orderId.toString().startsWith('#')) {
                        console.log('   ✅ OrderId matches GF pattern');
                        const value = this.flyerMapping.find(f => f.value === 'GF');
                        console.log('   🔍 Found GF flyer:', value);
                        if (value) {
                            flyers.push(value);
                        }
                    } else {
                        console.log('   ❌ No orderId pattern match');
                    }
                }

                // If external order -> check for reference
                if (reference) {
                    console.log('   Checking reference patterns...');
                    if (reference.toString().startsWith('#BP')) {
                        const value = this.flyerMapping.find(f => f.value === '#BP');
                        console.log('   Found BP flyer:', value);
                        if (value) {
                            flyers.push(value);
                        }
                    } else if (reference.toString().startsWith('#FW')) {
                        const value = this.flyerMapping.find(f => f.value === '#FW');
                        console.log('   Found FW flyer:', value);
                        if (value) {
                            flyers.push(value);
                        }
                    } else {
                        console.log('   No reference pattern match');
                    }
                }

                // Check for extra flyer conditions based on product tables
                const acrylTables = ['printcut', 'acrylglas_stundenplan', 'acrylglas_nachtlicht', 'acrylanhaenger', 'florithings_shapes', 'lyto_nachtlicht', 'lyto_printcut_heart'];
                const toniTables = ['toni_regal', 'lyto_toni_regal'];
                const moneyboxTables = ['lyto_spardose', 'spardose', 'uv_spardose', 'shirtracer_spardose'];

                console.log('   Checking product tables for special flyers...');

                // Check if any product has a translated.table in acryl or toni tables
                const hasAcrylTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && acrylTables.includes(table)) {
                        console.log(`   ✅ Found ACRYL table: ${table}`);
                        return true;
                    }
                    return false;
                });

                const hasToniTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && toniTables.includes(table)) {
                        console.log(`   ✅ Found TONI table: ${table}`);
                        return true;
                    }
                    return false;
                });

                const hasMoneyBoxTable = this.currentProducts.some(product => {
                    const table = product.translated?.table;
                    if (table && moneyboxTables.includes(table)) {
                        console.log(`   ✅ Found MONEYBOX table: ${table}`);
                        return true;
                    }
                    return false;
                });

                // Add ACRYL flyer if any product has acryl table
                if (hasAcrylTable) {
                    const acrylFlyer = this.flyerMapping.find(f => f.value === 'ACRYL');
                    if (acrylFlyer && !flyers.some(f => f.value === 'ACRYL')) {
                        console.log('   📋 Adding ACRYL flyer');
                        flyers.push(acrylFlyer);
                    }
                }

                // Add TONI flyer if any product has toni table
                if (hasToniTable) {
                    const toniFlyer = this.flyerMapping.find(f => f.value === 'TONI');
                    if (toniFlyer && !flyers.some(f => f.value === 'TONI')) {
                        console.log('   📋 Adding TONI flyer');
                        flyers.push(toniFlyer);
                    }
                }

                if (hasMoneyBoxTable) {
                    const moneyboxFlyer = this.flyerMapping.find(f => f.value === 'SPARDOSE');
                    if (moneyboxFlyer && !flyers.some(f => f.value === 'SPARDOSE')) {
                        console.log('   📋 Adding MONEYBOX flyer');
                        flyers.push(moneyboxFlyer);
                    }
                }

                console.log('🏁 currentFlyer FINAL RESULT (array):', flyers);
                return flyers;
            } catch (error) {
                console.error('❌ ERROR in currentFlyer computed property:', error);
                console.error('Error stack:', error.stack);
                return [];
            }
        },
        isMulticart() {
            // Return false if currentOrder or products are not loaded yet
            if (!this.currentOrder?.products || this.currentProducts.length === 0) {
                return false;
            }

            // Check if any product has isMultiCart property set to true
            return this.currentProducts.some(product => product.isMultiCart === true);
        },
        isSplittable() {
            // Order is splittable if no carton is available (carton selection failed)
            return !this.currentOrder?.packaging;
        },
        hasMissingItems() {
            if (!this.currentOrder || !this.currentOrder.products) {
                return false;
            }
            return this.currentOrder.products.some(x => x.status === 'missing');
        },
        itemsToScan() {
            // Sum up all quantities, not just count articles
            return this.currentProducts.reduce((total, product) => total + (product.qty || 1), 0);
        },
        scannedItems() {
            if (!this.currentOrder?.products) return 0;
            // Sum up all individual scans across all products
            return this.currentOrder.products.reduce((total, product) => total + (product.scannedCount || 0), 0);
        },
        currentPackageWeight() {
            // For now, keep this as 0 - weight calculation is a separate task
            return 0;
        },
        nextItemToScan() {
            // Find the first unscanned article
            const unscannedArticles = this.currentProducts.filter(product => {
                const scannedCount = product.scannedCount || 0;
                const requiredQty = product.qty || 1;
                return scannedCount < requiredQty;
            });

            if (unscannedArticles.length > 0) {
                const nextItem = unscannedArticles[0];
                const result = {
                    sku: nextItem.sku,
                    name: nextItem.attributes?.Wunschname || nextItem.translated?.sku || nextItem.sku,
                    qty: nextItem.qty || 1,
                    scannedCount: nextItem.scannedCount || 0
                };
                console.log('📋 nextItemToScan computed:', result);
                return result;
            }
            console.log('📋 nextItemToScan computed: null (no unscanned items)');
            return null;
        },
        isPrintable() {
            // All items must be scanned before printing (same for dev and production)
            const printable = this.scannedItems >= this.itemsToScan;
            console.log('📊 isPrintable check:', {
                mode: this.isDevelopmentMode ? 'development' : 'production',
                scannedItems: this.scannedItems,
                itemsToScan: this.itemsToScan,
                result: printable
            });
            return printable;
        },

        // Add computed properties for Socket.IO debugging
        orderSyncConnected() {
            return this.$store.getters['orderSync/isConnected'];
        },

        currentOrderRoom() {
            return this.$store.getters['orderSync/currentOrderRoom'];
        },

        orderSyncLastUpdated() {
            return this.$store.getters['orderSync/lastUpdated'];
        },

        // Computed properties for additional tracking IDs
        requiredTrackingIds() {
            // Return array of tracking IDs that need to be scanned
            if (this.currentOrder?.additionalTrackingIds && this.currentOrder.additionalTrackingIds.length > 0) {
                return this.currentOrder.additionalTrackingIds;
            }
            return [];
        },

        hasAdditionalTrackingIds() {
            return this.requiredTrackingIds.length > 0;
        },

        allTrackingIdsScanned() {
            if (!this.hasAdditionalTrackingIds) {
                // No additional tracking IDs required, use legacy behavior
                return this.trackingIdScanned;
            }

            // Check if all required tracking IDs have been scanned
            return this.requiredTrackingIds.every(trackingId =>
                this.scannedTrackingIds.includes(trackingId)
            );
        },

        remainingTrackingIds() {
            if (!this.hasAdditionalTrackingIds) return [];

            return this.requiredTrackingIds.filter(trackingId =>
                !this.scannedTrackingIds.includes(trackingId)
            );
        },

        trackingIdsProgress() {
            if (!this.hasAdditionalTrackingIds) return null;

            return {
                scanned: this.scannedTrackingIds.length,
                total: this.requiredTrackingIds.length,
                remaining: this.remainingTrackingIds.length
            };
        },

        // Mobile workflow computed properties
        mobilePickingComplete() {
            if (!this.isMobile || !this.currentOrder?.products) return false;

            // Check if all articles have been scanned in picking phase
            const totalRequired = this.itemsToScan;
            const totalScanned = Object.values(this.pickingScannedArticles).reduce((sum, count) => sum + count, 0);

            return totalScanned >= totalRequired;
        },

        mobilePackingComplete() {
            if (!this.isMobile || !this.currentOrder?.products) return false;

            // Check if all articles have been scanned in packing phase
            const totalRequired = this.itemsToScan;
            const totalScanned = Object.values(this.packingScannedArticles).reduce((sum, count) => sum + count, 0);

            return totalScanned >= totalRequired;
        },

        mobilePickingProgress() {
            if (!this.currentOrder?.products) return { scanned: 0, total: 0 };

            const totalRequired = this.itemsToScan;
            const totalScanned = Object.values(this.pickingScannedArticles).reduce((sum, count) => sum + count, 0);

            return {
                scanned: totalScanned,
                total: totalRequired
            };
        },

        mobilePackingProgress() {
            if (!this.currentOrder?.products) return { scanned: 0, total: 0 };

            const totalRequired = this.itemsToScan;
            const totalScanned = Object.values(this.packingScannedArticles).reduce((sum, count) => sum + count, 0);

            return {
                scanned: totalScanned,
                total: totalRequired
            };
        },

        // Swiss & Prime order detection
        isSwissOrder() {
            const shop = this.currentOrder?.shop;
            // Check both CountryCode and CountryISO2 (different order sources use different fields)
            const countryCode = this.currentOrder?.billbeeOrder?.ShippingAddress?.CountryCode ||
                               this.currentOrder?.billbeeOrder?.ShippingAddress?.CountryISO2;

            // Swiss orders can be from Muckelmeister OR ACS with CH country
            const isSwiss = (shop === 'muckelmeister' || shop === 'acs') && countryCode === 'CH';

            console.log('🇨🇭 isSwissOrder check:', {
                shop,
                countryCode,
                isMuckelmeister: shop === 'muckelmeister',
                isAcs: shop === 'acs',
                isCountryCH: countryCode === 'CH',
                result: isSwiss
            });

            return isSwiss;
        },

        isPrimeOrder() {
            return this.currentOrder?.shipping?.type === 'PRIME' &&
                   (this.currentOrder?.shop === 'amazon' ||
                    (this.currentOrder?.shop === 'acs' &&
                     this.currentOrder?.acsCustomer === 'Shirtracer'));
        },

        canScanTracking() {
            // Swiss orders: Need to print A4 docs first, then labels
            if (this.isSwissOrder) {
                // First scan after A4 print
                if (!this.swissDocumentsPrinted) return false;
                if (!this.swissTrackingScan) return true;
                // Second scan after label print
                if (!this.shippingLabelPrinted) return false;
                return true;
            }
            // Non-Swiss: Just need label printed
            return this.shippingLabelPrinted;
        },

        trackingScanPrompt() {
            if (this.isSwissOrder && !this.swissTrackingScan) {
                return 'Tracking ID vom A4 Dokument scannen';
            }
            return 'Tracking ID vom Versandlabel scannen';
        },

        // Check if mobile workflow is active (being used by another device)
        isMobileWorkflowActive() {
            if (this.isMobile) return false; // Mobile device itself doesn't need the indicator

            // Check if we have any picking or packing scans from mobile workflow
            const hasPickingScans = Object.keys(this.pickingScannedArticles).length > 0;
            const hasPackingScans = Object.keys(this.packingScannedArticles).length > 0;
            const isActive = hasPickingScans || hasPackingScans || this.mobileWorkflowStep > 1;

            console.log('[isMobileWorkflowActive]', {
                isMobile: this.isMobile,
                hasPickingScans,
                hasPackingScans,
                mobileWorkflowStep: this.mobileWorkflowStep,
                pickingScansCount: Object.keys(this.pickingScannedArticles).length,
                packingScansCount: Object.keys(this.packingScannedArticles).length,
                isActive
            });

            return isActive;
        },

        // Get current mobile workflow status text
        mobileWorkflowStatusText() {
            if (!this.isMobileWorkflowActive) return '';

            if (this.mobileWorkflowStep === 1) {
                const progress = this.mobilePickingProgress;
                return `Picken (${progress.scanned}/${progress.total})`;
            } else if (this.mobileWorkflowStep === 2) {
                return 'Walk to Table';
            } else if (this.mobileWorkflowStep === 3) {
                const progress = this.mobilePackingProgress;
                return `Packen (${progress.scanned}/${progress.total})`;
            } else if (this.mobileWorkflowStep === 4) {
                return 'Karton';
            } else if (this.mobileWorkflowStep === 5) {
                return 'Upsells';
            } else if (this.mobileWorkflowStep === 6) {
                return 'Flyer';
            } else if (this.mobileWorkflowStep === 7) {
                // Use Swiss-specific prompt if applicable
                return this.isSwissOrder ? this.trackingScanPrompt : 'Label Scan';
            } else if (this.mobileWorkflowStep === 8) {
                return 'Additional Tracking';
            } else if (this.mobileWorkflowStep === 9) {
                return 'Complete';
            }

            return '';
        }
    },
    watch: {
        messageSocket: {
            handler(socket) {
                if (socket) {
                    console.log('[MulticartWizard] Socket became available, registering alert listener');
                    socket.on('packlist-alert', this.handlePacklistAlert);
                }
            },
            immediate: true
        },
        currentPrinter: {
            handler(newVal) {
                if (newVal) {
                    this.currentPrinter = newVal;
                }
            },
            immediate: true,
            deep: true
        },
        scannedItems: {
            handler(newVal, oldVal) {
                if (newVal !== undefined && newVal !== oldVal) {
                    // Check if all items are scanned
                    if (newVal >= this.itemsToScan) {
                        console.log('✅ All items scanned! Ready to print.');

                        // Safety check: Ensure currentOrder is loaded
                        if (!this.currentOrder) {
                            console.log('⚠️ currentOrder not yet loaded, skipping auto-print');
                            return;
                        }

                        // Note: Swiss orders require manual print button click to attempt A4 document printing
                        // The system will automatically handle CN22 vs CN23 based on backend response
                        if (this.isSwissOrder) {
                            console.log('🇨🇭 Swiss order detected - user must click print button');
                        }
                    }
                }
            },
            immediate: true
        },
        currentProducts: {
            handler(newVal) {
                console.log('currentProducts watcher triggered:', newVal);
                if (newVal && Array.isArray(newVal)) {
                    console.log('currentProducts count:', newVal.length);
                } else {
                    console.warn('currentProducts is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        upsellItems: {
            handler(newVal) {
                console.log('upsellItems watcher triggered:', newVal);
                if (newVal && Array.isArray(newVal)) {
                    console.log('upsellItems count:', newVal.length);
                } else {
                    console.warn('upsellItems is not a valid array:', newVal);
                }
            },
            immediate: true
        },
        // Watch for order changes to join/leave order rooms
        currentOrder: {
            handler(newOrder, oldOrder) {
                console.log('[currentOrder Watcher] Order changed', {
                    hasNewOrder: !!newOrder,
                    hasOldOrder: !!oldOrder,
                    newOrderId: newOrder?.orderId,
                    oldOrderId: oldOrder?.orderId,
                    isSocketConnected: this.orderSyncConnected
                });

                // Leave previous order room if exists
                if (oldOrder?.orderId && oldOrder.orderId !== newOrder?.orderId) {
                    console.log('[currentOrder Watcher] Leaving previous order room:', oldOrder.orderId);
                    this.leaveOrderRoom();
                }

                // Join new order room if we have a new order
                if (newOrder?.orderId && newOrder.orderId !== oldOrder?.orderId) {
                    console.log('[currentOrder Watcher] ✅ Will join new order room in 500ms:', newOrder.orderId);

                    // Debug log additional tracking IDs
                    if (newOrder?.additionalTrackingIds) {
                        console.log('📋 Order has additional tracking IDs:', newOrder.additionalTrackingIds);
                        console.log('📋 Total tracking IDs required:', newOrder.additionalTrackingIds.length);
                    } else {
                        console.log('📋 Order has no additional tracking IDs - using single tracking ID mode');
                    }

                    // Small delay to ensure socket is connected
                    setTimeout(() => {
                        console.log('[currentOrder Watcher] 500ms elapsed, calling joinOrderRoom now');
                        this.joinOrderRoom(newOrder.orderId);

                        // Request current order state after joining room and order is loaded
                        setTimeout(() => {
                            this.$store.dispatch('orderSync/requestOrderState', newOrder.orderId);
                            this.applyScanProgressFromStore();
                        }, 2000); // Longer delay to ensure everything is ready
                    }, 500);
                } else {
                    console.log('[currentOrder Watcher] ❌ Not joining room - condition not met');
                }
            },
            deep: true
        },

        // Watch Socket.IO connection status for debugging
        orderSyncConnected: {
            handler(newVal, oldVal) {
                if (newVal && !oldVal) {
                    console.log('[MulticartWizard] ✅ OrderSync connected');
                }
            }
        },

        currentOrderRoom: {
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    console.log(`[MulticartWizard] Joined order room: ${newVal}`);
                }
            }
        },

        // Watch for incoming Socket.IO events from orderSync store
        '$store.state.orderSync.scanProgress': {
            handler(newScanProgress, oldScanProgress) {
                if (!newScanProgress || !this.currentOrder?.products) return;

                // Update local order data with scan progress from other devices
                Object.keys(newScanProgress).forEach(itemId => {
                    const progress = newScanProgress[itemId];

                    // Find the product in current order (try multiple matching strategies)
                    let productIndex = this.currentOrder.products.findIndex(p =>
                        (p.id || p._id || p.sku) === itemId
                    );

                    // If not found, try matching by SKU only
                    if (productIndex === -1) {
                        productIndex = this.currentOrder.products.findIndex(p => p.sku === itemId);
                    }

                    if (productIndex !== -1) {
                        this.$set(this.currentOrder.products[productIndex], 'scannedCount', progress.scannedCount || 0);
                        this.$set(this.currentOrder.products[productIndex], 'scanned', progress.scanned || false);
                        if (progress.scannedCount > 0) {
                            this.$set(this.currentOrder.products[productIndex], 'lastScanTime', new Date().toISOString());
                        }
                    }
                });
            },
            deep: true,
            immediate: true
        },

        '$store.state.orderSync.printStatus': {
            handler(newPrintStatus) {
                if (!newPrintStatus) return;

                // Update local print status (only if we're in tablet mode to avoid conflicts)
                if (this.isTablet) {
                    this.isPrintable = newPrintStatus.isPrintable || false;
                    this.isPrinting = newPrintStatus.isPrinting || false;
                    this.isPrinted = newPrintStatus.isPrinted || false;
                }
            },
            deep: true
        },

        '$store.state.orderSync.packingStatus': {
            handler(newPackingStatus) {
                if (!newPackingStatus) return;

                console.log('[MulticartWizard] Received packing status update:', newPackingStatus);

                // Show notification on tablet when order is packed
                if (this.isTablet && newPackingStatus.isPacked) {
                    this.showSnackbar('✅ Bestellung wurde verpackt', 'success');
                }
            },
            deep: true
        },

        '$store.state.orderSync.orderStatus': {
            handler(newStatus) {
                if (!newStatus) return;

                // Handle scan reset from other devices
                if (newStatus === 'scan-reset') {
                    this.resetScanning();

                    // Reset print status as well (but don't emit events to avoid loops)
                    this.isPrinted = false;
                    this.isPrinting = false;
                    this.trackingIdScanned = false;
                    this.scannedTrackingId = null;
                    this.scannedTrackingIds = [];

                    if (this.isTablet) {
                        this.showSnackbar('🔄 Scan-Fortschritt wurde zurückgesetzt', 'info');
                    }
                }

                // Handle order packed from other devices - load next order
                if (newStatus === 'order-packed') {
                    console.log('📦 [OrderSync] Order packed on another device - loading next order');
                    this.showSnackbar('📦 Bestellung wurde verpackt - lade nächste Bestellung', 'info');

                    // Load next order to stay in sync
                    this.getNextOrder();
                }
            }
        },

        // Watch for store scan progress changes and apply to local order
        '$store.state.orderSync.lastUpdated': {
            handler(newTime, oldTime) {
                if (!newTime || newTime === oldTime) return;

                // Trigger a check for any scan progress updates
                const scanProgress = this.$store.state.orderSync.scanProgress;
                if (scanProgress && this.currentOrder?.products) {
                    this.applyScanProgressFromStore();
                }
            }
        },

        // Removed auto-opening split modal watcher
        // Split modal should only be opened manually by user clicking the split button

        // Mobile workflow watchers
        isPrinted: {
            handler(newVal) {
                if (this.isMobile && newVal && this.mobileWorkflowStep === 7) {
                    // Label was printed, can now scan tracking
                    console.log('[Mobile Workflow] Label printed, waiting for tracking scan');
                }
            }
        },

        allTrackingIdsScanned: {
            handler(newVal) {
                if (this.isMobile && newVal) {
                    // All tracking IDs scanned
                    if (this.mobileWorkflowStep === 7) {
                        // Move to step 9 (or 8 if there are additional tracking IDs)
                        if (this.hasAdditionalTrackingIds && this.remainingTrackingIds.length > 0) {
                            this.mobileWorkflowStep = 8;
                        } else {
                            this.mobileWorkflowStep = 9;
                        }
                    } else if (this.mobileWorkflowStep === 8) {
                        // All additional tracking IDs scanned, move to complete
                        this.mobileWorkflowStep = 9;
                    }
                }
            }
        },

        mobilePickingComplete: {
            handler(newVal) {
                if (this.isMobile && newVal && this.mobileWorkflowStep === 1) {
                    console.log('[Mobile Workflow] Picking complete - auto-advancing to step 2');
                    // Auto-advance to step 2 (walk to table) when picking is complete
                    setTimeout(() => {
                        this.advanceMobileWorkflowStep();
                    }, 500);
                }
            }
        },

        currentOrder: {
            handler(newOrder, oldOrder) {
                // Reset mobile workflow when order changes
                if (this.isMobile && newOrder?.orderId !== oldOrder?.orderId) {
                    this.resetMobileWorkflow();
                }
            }
        },

        // Watch mobile workflow step changes and emit to sync with other devices
        mobileWorkflowStep: {
            handler(newStep, oldStep) {
                console.log('[Mobile Workflow Step Watcher]', {
                    isMobile: this.isMobile,
                    hasOrderId: !!this.currentOrder?.orderId,
                    orderId: this.currentOrder?.orderId,
                    oldStep,
                    newStep,
                    isConnected: this.orderSyncConnected
                });

                if (this.isMobile && this.currentOrder?.orderId && newStep !== oldStep) {
                    console.log(`[Mobile Workflow] ✅ Step changed: ${oldStep} → ${newStep}, emitting sync event`);
                    this.emitMobileWorkflowUpdate();
                } else {
                    console.log('[Mobile Workflow] ❌ Not emitting - conditions not met');
                }
            }
        },

        // Watch mobile picking scanned articles and emit to sync
        pickingScannedArticles: {
            handler(newVal, oldVal) {
                const hasChanged = JSON.stringify(newVal) !== JSON.stringify(oldVal);
                console.log('[Picking Scans Watcher]', {
                    isMobile: this.isMobile,
                    hasOrderId: !!this.currentOrder?.orderId,
                    orderId: this.currentOrder?.orderId,
                    hasChanged,
                    scannedCount: Object.keys(newVal).length,
                    isConnected: this.orderSyncConnected
                });

                if (this.isMobile && this.currentOrder?.orderId && hasChanged) {
                    console.log('[Mobile Workflow] ✅ Picking scans updated, emitting sync event', newVal);
                    this.emitMobileWorkflowUpdate();
                } else {
                    console.log('[Mobile Workflow] ❌ Not emitting picking scans - conditions not met');
                }
            },
            deep: true
        },

        // Watch mobile packing scanned articles and emit to sync
        packingScannedArticles: {
            handler(newVal, oldVal) {
                if (this.isMobile && this.currentOrder?.orderId && JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
                    console.log('[Mobile Workflow] Packing scans updated, emitting sync event');
                    this.emitMobileWorkflowUpdate();
                }
            },
            deep: true
        },

        // Watch for incoming mobile workflow updates from other devices
        '$store.state.orderSync.mobileWorkflowState': {
            handler(newState) {
                console.log('[Desktop Watcher] Mobile workflow state changed:', {
                    hasNewState: !!newState,
                    isMobile: this.isMobile,
                    newState: newState
                });

                if (!newState) {
                    console.log('[Desktop Watcher] ⚠️ newState is null/undefined, skipping');
                    return;
                }

                if (this.isMobile) {
                    console.log('[Desktop Watcher] ⚠️ This is a mobile device, skipping');
                    return;
                }

                console.log('[Desktop Watcher] ✅ Applying mobile workflow state from other device:', newState);

                // Apply the mobile workflow state from the mobile device using Vue.set for reactivity
                this.mobileWorkflowStep = newState.step || 1;

                // Use Vue.set to ensure reactivity for nested objects
                this.$set(this, 'pickingScannedArticles', { ...newState.pickingScannedArticles } || {});
                this.$set(this, 'packingScannedArticles', { ...newState.packingScannedArticles } || {});

                console.log('[Desktop Watcher] State applied:', {
                    step: this.mobileWorkflowStep,
                    pickingScansCount: Object.keys(this.pickingScannedArticles).length,
                    packingScansCount: Object.keys(this.packingScannedArticles).length,
                    pickingScannedArticles: this.pickingScannedArticles,
                    packingScannedArticles: this.packingScannedArticles
                });

                // Use $nextTick to ensure computed properties are re-evaluated after state changes
                this.$nextTick(() => {
                    console.log('[Desktop Watcher] After nextTick:', {
                        isMobileWorkflowActive: this.isMobileWorkflowActive,
                        mobileWorkflowStatusText: this.mobileWorkflowStatusText,
                        pickingScansCount: Object.keys(this.pickingScannedArticles).length,
                        packingScansCount: Object.keys(this.packingScannedArticles).length
                    });
                    // Force update to ensure badge appears
                    this.$forceUpdate();
                });
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        // Socket.io Alert Handler
        handlePacklistAlert(alert) {
            console.log('📢 Received packlist alert:', alert);
            this.currentAlert = alert;
            this.alertDialog = true;
        },
        onAlertDismissed() {
            this.alertDialog = false;
            this.currentAlert = null;
        },

        goBack() {
            // Smart back button: go back within step or between steps
            if (this.isMobile && this.mobileWorkflowStep > 1) {
                console.log('[Mobile Workflow] Going back from step', this.mobileWorkflowStep);

                // Check if we can go back within current step
                if (this.mobileWorkflowStep === 5) {
                    // Upsell step: go back within upsells if not at first one
                    const mobileWorkflowRef = this.$refs.mobileWorkflow;
                    if (mobileWorkflowRef && mobileWorkflowRef.currentUpsellIndex > 0) {
                        console.log('[Mobile Workflow] Going back within upsells');
                        mobileWorkflowRef.currentUpsellIndex--;
                        return;
                    }
                    // At first upsell -> go back to previous step (carton or packing)
                    if (this.currentOrder?.packaging) {
                        this.mobileWorkflowStep = 4;
                    } else {
                        this.mobileWorkflowStep = 3;
                    }
                } else if (this.mobileWorkflowStep === 6) {
                    // Flyer step: go back within flyers if not at first one
                    const mobileWorkflowRef = this.$refs.mobileWorkflow;
                    if (mobileWorkflowRef && mobileWorkflowRef.currentFlyerIndex > 0) {
                        console.log('[Mobile Workflow] Going back within flyers');
                        mobileWorkflowRef.currentFlyerIndex--;
                        return;
                    }
                    // At first flyer -> go back to previous step (upsells, carton, or packing)
                    if (this.upsellItems && this.upsellItems.length > 0) {
                        this.mobileWorkflowStep = 5;
                    } else if (this.currentOrder?.packaging) {
                        this.mobileWorkflowStep = 4;
                    } else {
                        this.mobileWorkflowStep = 3;
                    }
                } else if (this.mobileWorkflowStep === 2) {
                    // From walk-to-table -> back to picking
                    this.mobileWorkflowStep = 1;
                } else if (this.mobileWorkflowStep === 3) {
                    // From packing -> back to walk-to-table
                    this.mobileWorkflowStep = 2;
                } else if (this.mobileWorkflowStep === 4) {
                    // From carton -> back to packing
                    this.mobileWorkflowStep = 3;
                }
            } else {
                // Not in mobile workflow or at step 1 -> go to task choice
                this.$router.push('/warehouse/task-choice');
            }
        },

        // Mobile workflow methods
        async advanceMobileWorkflowStep() {
            console.log('[Mobile Workflow] Advancing from step', this.mobileWorkflowStep);

            if (this.mobileWorkflowStep === 1 && this.mobilePickingComplete) {
                // Step 1 -> 2: All articles picked, move to walk-to-table
                this.mobileWorkflowStep = 2;
            } else if (this.mobileWorkflowStep === 2) {
                // Step 2 -> 3: At packing table, start packing verification
                this.mobileWorkflowStep = 3;
            } else if (this.mobileWorkflowStep === 3 && this.mobilePackingComplete) {
                // Step 3 -> 4, 5, or 6: All articles verified
                // Check what needs to be confirmed next
                if (this.currentOrder?.packaging) {
                    this.mobileWorkflowStep = 4; // Show carton confirmation
                } else if (this.upsellItems && this.upsellItems.length > 0) {
                    this.mobileWorkflowStep = 5; // Show upsell confirmation
                } else if (this.currentFlyer && this.currentFlyer.length > 0) {
                    this.mobileWorkflowStep = 6; // Show flyer confirmation
                } else {
                    this.mobileWorkflowStep = 7; // Skip to label scan
                    // Auto-print based on order type
                    await this.mobileAutoPrint();
                }
            }
            // Steps 4-9 are handled by confirmation methods
        },

        async confirmCarton() {
            console.log('[Mobile Workflow] Carton confirmed');
            // Step 4 -> 5 or 6: Check if upsells or flyers exist
            if (this.upsellItems && this.upsellItems.length > 0) {
                this.mobileWorkflowStep = 5; // Show upsell confirmation
            } else if (this.currentFlyer && this.currentFlyer.length > 0) {
                this.mobileWorkflowStep = 6; // Show flyer confirmation
            } else {
                this.mobileWorkflowStep = 7; // Skip to label scan
                await this.mobileAutoPrint();
            }
        },

        async confirmUpsells() {
            console.log('[Mobile Workflow] Upsells confirmed');
            // Step 5 -> 6 or 7: Check if flyers exist
            if (this.currentFlyer && this.currentFlyer.length > 0) {
                this.mobileWorkflowStep = 6; // Show flyer confirmation
            } else {
                this.mobileWorkflowStep = 7; // Skip to label scan
                await this.mobileAutoPrint();
            }
        },

        async confirmFlyer() {
            console.log('[Mobile Workflow] Flyers confirmed, advancing to label scan');
            // Step 6 -> 7: Flyers confirmed, trigger print and move to label scan
            this.mobileWorkflowStep = 7;
            // Auto-print when flyers are confirmed
            await this.mobileAutoPrint();
        },

        resetMobileWorkflow() {
            console.log('[Mobile Workflow] Resetting workflow state');
            this.mobileWorkflowStep = 1;
            this.pickingScannedArticles = {};
            this.packingScannedArticles = {};

            // Reset Swiss & Prime workflow states
            this.swissDocumentsPrinted = false;
            this.shippingLabelPrinted = false;
            this.swissTrackingScan = false;
            this.finalTrackingScan = false;
        },

        // Hardcoded trackStart functions (from old packlist)
        async handlePackstats() {
            // Only store the view start time, don't actually start tracking yet
            const currentPackSession = localStorage.getItem('lastTimestamp');

            if (currentPackSession && this.user?.id === JSON.parse(currentPackSession).userId) {
                const packSession = JSON.parse(currentPackSession);
                this.itemViewStartTime = new Date(packSession.packStart);
                console.log('📊 Using existing session start time for display', this.currentPacklistItemId, packSession.packStart);
            } else {
                const viewStartTime = new Date();
                this.itemViewStartTime = viewStartTime;

                const newPackSession = {
                    userId: this.user?.id,
                    packStart: viewStartTime
                };

                localStorage.setItem('lastTimestamp', JSON.stringify(newPackSession));
                console.log('📊 Stored new view start time (not tracking yet)', this.currentPacklistItemId, viewStartTime);
            }

            // Reset packing started flag for new item
            this.packingStarted = false;
            await this.startActualPacking();
        },

        async startActualPacking() {
            // This is called on first pack attempt
            if (!this.packingStarted && this.itemViewStartTime && this.currentPacklistItemId) {
                console.log('🏁 Starting actual pack tracking with stored time:', this.currentPacklistItemId, this.itemViewStartTime);
                await PacklistService.trackStart(this.currentPacklistItemId, this.itemViewStartTime);
                this.packingStarted = true;
                console.log('✅ Pack tracking started successfully');
            } else {
                console.log('⏭️ Packing already started or no start time available', this.packingStarted, this.itemViewStartTime, this.currentPacklistItemId);
            }
        },

        //Api - GET
        async silentRefreshOrder() {
            try {
                console.log('🔄 Silent refresh - getting updated order data without state changes');

                // Get the same order again to pick up any changes (like tracking ID)
                // IMPORTANT: Use getNextMulticartOrder to get packaging and packlistItemId
                const response = await PacklistService.getNextMulticartOrder(this.currentOrder.orderId, this.currentShelf);

                if (response.status === 200 && response.data && response.data.order) {
                    // Update order data in place without affecting UI states
                    const updatedOrder = response.data.order;

                    // CRITICAL: Preserve packlistItemId (needed for PackTracker.endPackTracking)
                    // If backend doesn't return it, preserve from current order
                    if (!updatedOrder.packlistItemId && this.currentPacklistItemId) {
                        updatedOrder.packlistItemId = this.currentPacklistItemId;
                        console.log('✅ Preserved packlistItemId:', this.currentPacklistItemId);
                    } else if (updatedOrder.packlistItemId) {
                        // Update our stored reference
                        this.currentPacklistItemId = updatedOrder.packlistItemId;
                        console.log('✅ Updated packlistItemId from backend:', updatedOrder.packlistItemId);
                    }

                    // Preserve slot info
                    updatedOrder.slot = this.currentOrder.slot;

                    // Preserve packaging info (carton selection) - CRITICAL for isSplittable check
                    if (this.currentOrder.packaging && !updatedOrder.packaging) {
                        updatedOrder.packaging = this.currentOrder.packaging;
                        console.log('✅ Preserved packaging data during silent refresh:', this.currentOrder.packaging);
                    }

                    // Preserve scan states and packlistItemId from current order
                    if (this.currentOrder.products && updatedOrder.products) {
                        updatedOrder.products.forEach((updatedProduct, index) => {
                            const currentProduct = this.currentOrder.products.find(p =>
                                p.designId === updatedProduct.designId && p.sku === updatedProduct.sku
                            );
                            if (currentProduct) {
                                // Preserve all scan-related states
                                updatedProduct.scannedCount = currentProduct.scannedCount || 0;
                                updatedProduct.scanned = currentProduct.scanned || false;
                                updatedProduct.scanTime = currentProduct.scanTime;
                                updatedProduct.lastScanTime = currentProduct.lastScanTime;
                                updatedProduct.shelfHashcode = currentProduct.shelfHashcode;

                                // Preserve packlistItemId
                                if (currentProduct.packlistItemId) {
                                    updatedProduct.packlistItemId = currentProduct.packlistItemId;
                                }
                            }
                        });
                    }

                    this.currentOrder = updatedOrder;
                    console.log('✅ Order data silently refreshed - tracking ID, packaging, and packlistItemId preserved');

                    console.log('📊 States after silent refresh:', {
                        isPrinted: this.isPrinted,
                        isPrinting: this.isPrinting,
                        nextItemToScan: !!this.nextItemToScan,
                        currentOrder: !!this.currentOrder,
                        hasPackaging: !!this.currentOrder.packaging,
                        hasPacklistItemId: !!this.currentPacklistItemId,
                        packlistItemId: this.currentPacklistItemId,
                        isSplittable: this.isSplittable
                    });
                } else {
                    console.warn('⚠️ Silent refresh failed, keeping current order data');
                }
            } catch (error) {
                console.error('❌ Error during silent refresh:', error);
                // Don't show error to user since this is a background operation
            }
        },

        async getNextOrder() {
            this.isLoading = true;

            // Reset error state
            this.errorState = {
                type: 'empty',
                message: '',
                orderId: null
            };

            try {
                let orderIdInUrl = this.$route.query.orderId;
                const response = await PacklistService.getNextMulticartOrder(orderIdInUrl, this.currentShelf);

                if (response.status === 200) {
                    if (response.data.order) {
                        // Reset state before loading new order
                        this.resetOrderSpecificState();

                        this.currentOrder = response.data.order;
                        this.currentSlot = response.data.order.slot;
                        this.currentPacklistItemId = response.data.order.packlistItemId;

                        console.log('📦 Next multicart order:', response.data.order.orderId, response.data.order.slot);

                        // Join order room immediately for real-time sync
                        console.log('📦 Joining order room for real-time sync:', response.data.order.orderId);
                        this.$nextTick(() => {
                            setTimeout(() => {
                                this.joinOrderRoom(response.data.order.orderId);
                            }, 500);
                        });
                        console.log('📦 PacklistItems array from backend:', response.data.order.packlistItems);
                        console.log('📦 PacklistItemId assigned:', {
                            packlistItemId: this.currentPacklistItemId,
                            fromBackend: response.data.order.packlistItemId,
                            hasPacklistItemId: !!this.currentPacklistItemId,
                            packlistItemsCount: response.data.order.packlistItems?.length || 0
                        });

                        // Attach orderId and packlistItemId to each product for easier matching
                        const orderIdForProducts = response.data.order.orderId;
                        if (response.data.order.packlistItems && response.data.order.packlistItems.length > 0) {
                            response.data.order.products.forEach(product => {
                                // Ensure orderId is at root level for scan matching
                                if (!product.orderId) {
                                    product.orderId = orderIdForProducts;
                                }

                                const packlistItem = response.data.order.packlistItems.find(item => item.sku === product.sku);
                                if (packlistItem) {
                                    product.packlistItemId = packlistItem.packlistItemId;
                                    console.log(`✅ Attached packlistItemId ${packlistItem.packlistItemId} to product ${product.sku}`);
                                } else {
                                    console.warn(`⚠️ No packlistItemId found for product ${product.sku}`);
                                }
                            });

                            // If no main packlistItemId, use the first one from the array
                            if (!this.currentPacklistItemId && response.data.order.packlistItems.length > 0) {
                                this.currentPacklistItemId = response.data.order.packlistItems[0].packlistItemId;
                                console.log(`✅ Using first packlistItemId as fallback: ${this.currentPacklistItemId}`);
                            }
                        }

                        // Warn if packlistItemId is still missing
                        if (!this.currentPacklistItemId) {
                            console.error('⚠️ WARNING: PacklistItemId is undefined! Pack tracking will fail!', {
                                orderId: response.data.order.orderId,
                                hasPacklistItems: !!response.data.order.packlistItems,
                                packlistItemsLength: response.data.order.packlistItems?.length || 0
                            });
                        }
                        console.log('📦 Order packaging:', {
                            hasPackaging: !!response.data.order.packaging,
                            packaging: response.data.order.packaging,
                            isSplittable: !response.data.order.packaging
                        });

                        // Start pack tracking (hardcoded trackStart from old packlist)
                        if (this.currentPacklistItemId && this.user) {
                            await this.handlePackstats();
                        }

                        // Stop loading immediately - don't wait for packstats
                        this.isLoading = false;

                        // Check for warranty and auto-open modal
                        this.$nextTick(() => {
                            if (this.currentOrder?.warranty || this.currentOrder?.warrantyReason) {
                                console.log('🟠 Warranty detected in MulticartWizard - auto-opening warranty modal');
                                this.openWarrantyModal();
                            }

                            // Check if order is splittable (no carton found) - FORCE split before continuing
                            if (this.isSplittable) {
                                console.log('⚠️ No carton found - opening split modal (REQUIRED before printing)');
                                this.showSnackbar('⚠️ Kein Karton gefunden - Paket muss aufgeteilt werden', 'warning', 5000);
                                this.openSplitModal();
                            }
                        });

                        // Initialize pack tracking for new order via EventBus
                        console.log('📊 Pack tracking initialization check:', {
                            hasCurrentUser: !!this.currentUser,
                            hasPacklistItemId: !!this.currentPacklistItemId,
                            packlistItemId: this.currentPacklistItemId,
                            orderId: this.currentOrder?.orderId
                        });

                        if (this.currentUser && this.currentPacklistItemId) {
                            console.log('✅ Starting pack tracking for packlistItemId:', this.currentPacklistItemId);
                            await PackTracker.startPackTracking(this.currentPacklistItemId);
                        } else {
                            console.error('❌ Cannot start pack tracking - missing user or packlistItemId:', {
                                currentUser: !!this.currentUser,
                                currentPacklistItemId: this.currentPacklistItemId,
                                reason: !this.currentUser ? 'No user' : 'No packlistItemId'
                            });
                        }

                        // Request scan progress state after order is loaded (for tablet reloads)
                        setTimeout(() => {
                            this.requestOrderState();
                        }, 2000);
                    } else {
                        // No order available
                        this.currentOrder = null;
                        this.errorState.type = 'empty';
                        this.isLoading = false;
                        console.log('No ready orders found');
                    }
                } else {
                    this.currentOrder = null;
                    this.errorState.type = 'empty';
                    this.isLoading = false;
                }

            } catch (error) {
                console.error('Error getting next multicart order:', error);
                this.currentOrder = null;
                this.isLoading = false;

                // Parse error response and set appropriate error state
                if (error.response) {
                    const errorData = error.response.data;
                    const statusCode = error.response.status;
                    const errorMessage = errorData.error || error.message;

                    if (statusCode === 404) {
                        // Order not found
                        if (errorMessage.includes('not found in database')) {
                            this.errorState.type = 'not_found';
                            this.errorState.message = 'Die Bestellung konnte in der Datenbank nicht gefunden werden.';
                        } else if (errorMessage.includes('not in multicart shelf')) {
                            this.errorState.type = 'not_in_shelf';
                            this.errorState.message = 'Die Bestellung befindet sich nicht im Multicart-Regal.';
                        } else if (errorMessage.includes('not found')) {
                            this.errorState.type = 'not_found';
                            this.errorState.message = errorMessage;
                        } else {
                            this.errorState.type = 'not_found';
                            this.errorState.message = errorMessage;
                        }
                    } else if (statusCode === 400) {
                        // Order not ready
                        this.errorState.type = 'not_ready';
                        this.errorState.message = errorMessage;
                    } else {
                        // Server error
                        this.errorState.type = 'server_error';
                        this.errorState.message = errorMessage;
                    }

                    // Store orderId if it was in the URL
                    if (this.$route.query.orderId) {
                        this.errorState.orderId = this.$route.query.orderId;
                    }

                    this.showSnackbar(`❌ ${errorMessage}`, 'error', 5000);
                } else {
                    // Network or other error
                    this.errorState.type = 'server_error';
                    this.errorState.message = 'Verbindung zum Server fehlgeschlagen.';
                    this.showSnackbar('❌ Verbindung zum Server fehlgeschlagen', 'error');
                }
            }
        },

        //State management
        resetOrderSpecificState() {
            // Reset all order-specific state when switching orders
            this.isPrinted = false;
            this.isPrinting = false;
            this.isPrintable = false;
            this.isScanning = false;
            this.packingStarted = false;
            this.itemViewStartTime = null;
            this.trackingIdScanned = false;
            this.scannedTrackingId = null;
            this.scannedTrackingIds = []; // Reset additional tracking IDs
            this.requiredTrackingScans = 1;  // Reset to default
            this.resetScanning();

            // Reset Swiss & Prime workflow states
            this.swissDocumentsPrinted = false;
            this.shippingLabelPrinted = false;
            this.swissTrackingScan = false;
            this.finalTrackingScan = false;
            this.swissDocumentUrls = [];
            this.shippingLabelUrls = [];

            // Reset mobile workflow state
            if (this.isMobile) {
                this.resetMobileWorkflow();
            }

            // Note: isFirstOrder is NOT reset here - only in pack() after first completion
            // Note: currentSlot is NOT reset here - it will be updated when new order loads
            console.log('🔄 Order-specific state reset');
        },

        //Pack tracking methods (same as PacklistRoot)
        async loadPackStats() {
            try {
                const response = await PacklistService.getPackStats();
                if (response?.data) {
                    this.currentPackstats.count = response.data.count;
                    this.currentPackstats.time = response.data.time;
                }
            } catch (error) {
                console.error('Error loading pack stats:', error);
            }
        },


        //Scanning Methods
        // Note: parseScanData method is defined below in the processScan section

        //Api - POST
        async print() {
            console.log('🖨️ PRINT BUTTON CLICKED');

            this.isPrinting = true;

            if (!this.currentPrinter) {
                this.openSettingsModal();
                this.isPrinting = false;
                return;
            }

            console.log('🖨️ Print workflow state:', {
                hasPackaging: !!this.currentOrder.packaging,
                packaging: this.currentOrder.packaging,
                isSplittable: this.isSplittable,
                isSwiss: this.isSwissOrder,
                isPrime: this.isPrimeOrder,
                swissDocumentsPrinted: this.swissDocumentsPrinted,
                shippingLabelPrinted: this.shippingLabelPrinted,
                swissTrackingScan: this.swissTrackingScan,
                shop: this.currentOrder?.shop,
                countryCode: this.currentOrder?.billbeeOrder?.ShippingAddress?.CountryCode
            });

            try {
                // Swiss workflow: Print A4 documents first
                if (this.isSwissOrder && !this.swissDocumentsPrinted) {
                    console.log('🖨️ Swiss order detected - printing A4 documents first');
                    await this.printSwissDocuments();
                    this.showSnackbar('✅ Swiss documents printed - scan tracking ID to continue', 'info');
                    return; // Wait for tracking scan before printing shipping label
                }

                // Non-Swiss workflow OR Swiss after first tracking scan
                console.log('🖨️ Printing shipping label');
                await this.printShippingLabel();

            } catch (error) {
                this.showSnackbar('❌ Fehler beim Drucken des Labels', 'error');
                console.error('Error printing:', error);
                this.isPrinting = false;
            }
        },

        async printSwissDocuments() {
            if (!this.currentPrinter?.a4) {
                this.showSnackbar('❌ No A4 printer configured', 'error');
                this.openSettingsModal();
                return;
            }

            this.isPrinting = true;
            try {
                const response = await PacklistService.printMulticartSwissDocuments(
                    this.currentOrder.orderId,
                    this.currentPrinter.a4
                );

                if (response.status === 200) {
                    this.showSnackbar(`✅ ${response.data.documentCount} Swiss document(s) printed`, 'success');
                    this.swissDocumentsPrinted = true;
                    this.swissDocumentUrls = response.data.documentUrls || [];

                    // For Swiss orders: require 2 tracking scans
                    this.requiredTrackingScans = 2;
                    console.log(`Swiss tracking setup: tracking must be scanned ${this.requiredTrackingScans} times`);
                    console.log(`Swiss documents printed for order ${this.currentOrder.orderId}`, this.swissDocumentUrls);
                }
            } catch (error) {
                console.error('Error printing Swiss documents:', error);

                // Handle specific error codes
                if (error.response?.status === 404 && error.response?.data?.errorCode === 'NO_SWISS_DOCUMENTS') {
                    // CN22 / Warenpost order - no A4 documents needed
                    console.log(`Order ${this.currentOrder.orderId} is CN22 (Warenpost) - skipping A4 documents`);
                    this.showSnackbar('ℹ️ CN22 order - no A4 documents needed, auto-printing shipping label', 'info');

                    // Mark as "printed" to allow workflow to continue
                    this.swissDocumentsPrinted = true;
                    this.swissDocumentUrls = [];

                    // Mark Swiss first tracking scan as done (since we're skipping A4 docs)
                    this.swissTrackingScan = true;

                    // CN22 requires only 1 tracking scan (just the label)
                    this.requiredTrackingScans = 1;
                    console.log('CN22 tracking setup: tracking must be scanned 1 time');

                    // Always auto-print shipping label for CN22 orders
                    console.log('🖨️ Auto-printing shipping label for CN22 order');
                    await this.printShippingLabel();
                } else if (error.response?.status === 404 && error.response?.data?.errorCode === 'ORDER_NOT_FOUND') {
                    // Order doesn't exist - real error
                    this.showSnackbar('❌ Order not found', 'error');
                } else if (error.response?.status === 400 && error.response?.data?.errorCode === 'MISSING_PRINTER') {
                    // Missing printer - shouldn't happen due to earlier check, but handle it
                    this.showSnackbar('❌ Printer configuration missing', 'error');
                } else {
                    // Generic error
                    this.showSnackbar('❌ Error printing Swiss documents', 'error');
                }
            } finally {
                this.isPrinting = false;
            }
        },

        async printShippingLabel() {
            if (!this.currentOrder?.orderId) {
                this.showSnackbar('❌ No order loaded', 'error');
                console.error('printShippingLabel called without order');
                return;
            }

            if (!this.currentPrinter?.url) {
                this.showSnackbar('❌ No label printer configured', 'error');
                this.openSettingsModal();
                return;
            }

            this.isPrinting = true;
            try {
                const response = await PacklistService.printMulticartShippingLabel(
                    this.currentOrder.orderId,
                    this.currentPrinter.url,
                    false  // under3cm always false for multicart
                );

                if (response.status === 200) {
                    const labelCount = response.data.labelCount;
                    const isPrime = response.data.isPrime;

                    let message = `✅ ${labelCount} label(s) printed`;
                    if (isPrime) {
                        message += ' (incl. Prime label)';
                    }

                    this.showSnackbar(message, 'success');
                    this.shippingLabelPrinted = true;
                    this.isPrinted = true;
                    this.shippingLabelUrls = response.data.labelUrls || [];

                    // Emit socket event for real-time sync
                    this.emitPrintStatusUpdate();

                    console.log(`Shipping labels printed for order ${this.currentOrder.orderId}, isPrime: ${isPrime}`, this.shippingLabelUrls);

                    if (!this.tracking) {
                        console.log('⚠️ No tracking ID yet - calling silent refresh');
                        await this.silentRefreshOrder();
                    }
                }
            } catch (error) {
                console.error('Error printing shipping label:', error);
                this.showSnackbar('❌ Error printing shipping label', 'error');
            } finally {
                this.isPrinting = false;
            }
        },

        // Mobile workflow auto-print logic
        async mobileAutoPrint() {
            console.log('[Mobile Workflow] Auto-printing for order type:', {
                isSwiss: this.isSwissOrder,
                isPrime: this.isPrimeOrder
            });

            if (this.isSwissOrder) {
                // Swiss workflow: Print A4 documents first, then wait for tracking scan
                await this.printSwissDocuments();
            } else {
                // Non-Swiss workflow: Print shipping label directly
                await this.printShippingLabel();
            }
        },

        async pack() {
            if (this.isDevelopmentMode) this.isPrinted = true; // Auto-mark as printed in dev mode
            
            if (this.isPrinting || !this.isPrinted) {
                this.showSnackbar('❌ Bitte zuerst Label drucken', 'warning');
                return;
            };

            // In production mode, require all tracking IDs to be scanned before packing
            if (!this.isDevelopmentMode && !this.allTrackingIdsScanned) {
                if (this.hasAdditionalTrackingIds) {
                    const remaining = this.remainingTrackingIds.length;
                    this.showSnackbar(`❌ Bitte noch ${remaining} Tracking ID${remaining > 1 ? 's' : ''} scannen`, 'warning');
                } else {
                    this.showSnackbar('❌ Bitte zuerst Tracking ID scannen', 'warning');
                }
                return;
            }

            try {
                console.log('📦 Pack button pressed - finishing pack process');
                console.log('📊 Pack debug info:', {
                    orderId: this.currentOrder?.orderId,
                    packlistItemId: this.currentPacklistItemId,
                    hasCurrentOrder: !!this.currentOrder,
                    orderPacklistItemId: this.currentOrder?.packlistItemId
                });

                // Validate we have a packlistItemId
                if (!this.currentPacklistItemId) {
                    console.error('❌ No packlistItemId available for pack tracking!');
                    // Try to use order's packlistItemId as fallback
                    if (this.currentOrder?.packlistItemId) {
                        this.currentPacklistItemId = this.currentOrder.packlistItemId;
                        console.log('✅ Using order.packlistItemId as fallback:', this.currentPacklistItemId);
                    } else {
                        throw new Error('No packlistItemId available - cannot complete pack tracking');
                    }
                }

                console.log('🏁 Calling trackEnd for order:', this.currentOrder.orderId, 'with packlistItemId:', this.currentPacklistItemId);
                await PackTracker.endPackTracking(this.currentPacklistItemId);

                // Show loading overlay immediately to cover state resets
                this.isLoading = true;
                console.log('📦 Pack complete - loading next order...');

                // Emit socket event for real-time sync (non-blocking)
                this.$store.dispatch('orderSync/emitPackStatusUpdate', {
                    orderId: this.currentOrder.orderId,
                    packingStatus: {
                        isPackable: true,
                        isPacked: true
                    }
                });

                // Mark that this is no longer the first order
                this.isFirstOrder = false;

                // Emit pack complete event to sync all devices
                this.$store.dispatch('orderSync/emitOrderPacked', {
                    orderId: this.currentOrder.orderId
                });

                // Unlock shelf (non-blocking, can run in background)
                PacklistService.unlockMulticartShelf(this.currentSlot).catch(err => {
                    console.warn('Failed to unlock shelf:', err);
                });

                // Load pack stats in background (non-blocking)
                this.loadPackStats().catch(err => {
                    console.warn('Failed to load pack stats:', err);
                });

                // Refresh to next order (this will handle loading state)
                await this.getNextOrder();

                // Show success message after new order is loaded
                this.showSnackbar(`✅ Bestellung erfolgreich verpackt`, 'success');

                console.log(`✅ Package packed and next order loaded!`);
            } catch (error) {
                this.isLoading = false; // Ensure loading state is cleared on error
                this.showSnackbar('❌ Fehler beim Verpacken', 'error');
                console.error('Error packing item:', error);
            }
        },
        async resetArticle(article) {
            try {
                console.log('Opening reset dialog for article:', article);
                this.selectedArticleForReset = article;
                this.damageResetOnly = true; // Default to reset only mode
                this.isDamagedDialogOpen = true;
            } catch (error) {
                console.error('Error opening reset dialog:', error);
            }
        },

        openBulkResetDialog() {
            // Open reset dialog without a specific article (bulk reset mode)
            // In multicart mode, show all order articles
            console.log('Opening bulk reset dialog');
            this.selectedArticleForReset = null; // No specific article
            this.damageResetOnly = true; // Default to reset only mode
            this.isDamagedDialogOpen = true;
        },
        //Scanner Methods (via scannerInputMixin)
        /**
         * Handle scan from DataWedge (called by scannerInputMixin)
         * This method is called when a complete scan is received from TC200 or TC22
         */
        onScanReceived(scannedValue) {
            console.log('🔍 [MulticartWizard] Processing scan from DataWedge:', scannedValue);
            console.log('📏 [MulticartWizard] Scan length:', scannedValue.length);
            console.log('🔢 [MulticartWizard] Char codes:', Array.from(scannedValue).map(c => `${c}(${c.charCodeAt(0)})`).join(' '));

            // Prevent actions during printing/processing
            if (this.isPrinting) {
                console.log('⚠️ Scan ignored: Printing in progress');
                return;
            }

            // Check for admin key scan BEFORE processing as normal scan
            if (this.checkAdminKeyScan(scannedValue)) {
                console.log('🐛 [MulticartWizard] Admin key detected, debug mode activated');
                return; // Don't process as normal scan
            }

            // Normalize German keyboard layout to US layout
            const normalizedScan = this.normalizeGermanKeyboard(scannedValue);
            console.log('📋 Normalized scan data:', normalizedScan, '(original:', scannedValue, ')');
            console.log('🔢 Normalized char codes:', Array.from(normalizedScan).map(c => `${c}(${c.charCodeAt(0)})`).join(' '));

            // Debug log to mobile workflow
            if (this.isMobile && this.$refs.mobileWorkflow) {
                this.$refs.mobileWorkflow.addDebugLog(`🔍 Raw: "${scannedValue}"`, 'info');
                this.$refs.mobileWorkflow.addDebugLog(`📋 Normalized: "${normalizedScan}"`, 'info');
            }

            // Block article scanning if order is splittable (no carton found)
            if (this.isSplittable && !this.isTrackingIdScan(normalizedScan)) {
                console.log('⚠️ Order is splittable, blocking article scan');
                if (this.isMobile && this.$refs.mobileWorkflow) {
                    this.$refs.mobileWorkflow.addDebugLog('❌ Blocked: Order needs split', 'error');
                }
                this.showSnackbar('❌ Bitte erst Paket aufteilen', 'warning');
                this.openSplitModal();
                return;
            }

            // Set scanning state
            this.isScanning = true;

            // Process the normalized scan
            this.processNormalizedScan(normalizedScan);
        },

        // Check if scanned value is an admin RFID key to enable debug mode
        checkAdminKeyScan(scanValue) {
            if (!scanValue) return false;

            // Admin keys that can activate debug mode
            const adminKeys = [
                'KABA:MF,I:02120c0000096e,V:1,T:A16',
                'KABA:MF,I:02132D00000DD7,V:1,T:A16'
            ];

            // Check if scanned value matches any admin key
            if (adminKeys.includes(scanValue)) {
                console.log('🐛 [MulticartWizard] Admin key detected! Enabling debug mode');
                if (this.isMobile && this.$refs.mobileWorkflow) {
                    this.$refs.mobileWorkflow.enableDebugMode();
                }
                return true; // This scan was handled as debug mode activation
            }

            return false; // Not an admin key, continue normal scan processing
        },


        normalizeGermanKeyboard(scanString) {
            // Convert German keyboard layout to US keyboard layout
            // German keyboard sends: ' instead of | and ? instead of _
            // This happens because barcode scanners send keycodes that are interpreted by the OS keyboard layout
            if (!scanString) return scanString;

            return scanString
                .replace(/'/g, '|')  // Replace apostrophe with pipe
                .replace(/\?/g, '_'); // Replace question mark with underscore
        },

        async processNormalizedScan(scanData) {
            console.log('🔍 Processing normalized scan:', scanData);

            try {
                // Check if this is a tracking ID scan
                if (this.isTrackingIdScan(scanData)) {
                    console.log('📋 Detected tracking ID scan');
                    if (this.isMobile && this.$refs.mobileWorkflow) {
                        this.$refs.mobileWorkflow.addDebugLog('📋 Type: Tracking ID', 'info');
                    }
                    await this.handleTrackingIdScan(scanData);
                } else {
                    console.log('📦 Detected article/order scan, parsing...');
                    if (this.isMobile && this.$refs.mobileWorkflow) {
                        this.$refs.mobileWorkflow.addDebugLog('📦 Type: Article/Order', 'info');
                    }
                    // Parse as article scan
                    const parsedData = this.parseScanData(scanData);

                    if (parsedData) {
                        console.log('✅ Parsed article scan data:', parsedData);
                        if (this.isMobile && this.$refs.mobileWorkflow) {
                            this.$refs.mobileWorkflow.addDebugLog(`✅ Parsed: ${parsedData.orderIdOnly ? 'OrderID=' + parsedData.orderId : 'SKU=' + parsedData.sku}`, 'success');
                        }
                        await this.handleScannedData(parsedData);
                    } else {
                        console.error('❌ Invalid barcode format:', scanData);
                        if (this.isMobile && this.$refs.mobileWorkflow) {
                            this.$refs.mobileWorkflow.addDebugLog(`❌ Invalid format: "${scanData}"`, 'error');
                        }
                        this.showScanError('wrong-article', {
                            scannedSku: scanData,
                            expectedSku: 'Valid barcode format'
                        });
                    }
                }
            } catch (error) {
                console.error('❌ Error processing scan:', error);
                if (this.isMobile && this.$refs.mobileWorkflow) {
                    this.$refs.mobileWorkflow.addDebugLog(`❌ Error: ${error.message}`, 'error');
                }
                this.showScanError('wrong-article', {
                    scannedSku: scanData,
                    expectedSku: 'Valid barcode'
                });
            } finally {
                // Reset scanning state
                this.isScanning = false;
            }
        },

        parseScanData(scanString) {
            console.log('🔍 [parseScanData] Input:', scanString);

            // Parse format for US keyboard: MM12345|box_teein|A1|1234567890
            const parts = scanString.split('|');

            if (parts.length >= 4) {
                console.log('✅ [parseScanData] Detected full barcode (pipe-delimited)');
                return {
                    orderId: parts[0],
                    sku: parts[1],
                    location: parts[2],
                    hashcode: parts[3]
                };
            }

            // Fallback: try apostrophe delimiter for German keyboard scans
            // German keyboard: MM12345'box?teein'A1'1234567890
            const altParts = scanString.split("'");
            if (altParts.length >= 4) {
                console.log('✅ [parseScanData] Detected full barcode (apostrophe-delimited)');
                return {
                    orderId: altParts[0],
                    sku: altParts[1].replace(/\?/g, '_'), // Replace ? with _ for SKU
                    location: altParts[2],
                    hashcode: altParts[3]
                };
            }

            // NEW: Support orderID-only scanning for transition period
            // Check if this looks like an orderID (e.g., MM12345, #MM12345, 465446)
            // OrderIDs can be:
            // - Letter prefix + numbers (MM12345, GF12345): 1-3 letters, 4-8 numbers
            // - Pure numbers (465446): 5-8 digits
            // - With optional # prefix (#MM12345)
            const orderIdPattern = /^#?(?:[A-Z]{1,3}\d{4,8}|\d{5,8})$/i;
            if (orderIdPattern.test(scanString)) {
                console.log('✅ [parseScanData] Detected orderID-only scan:', scanString);
                return {
                    orderId: scanString.replace('#', ''), // Remove # if present
                    orderIdOnly: true // Flag to indicate this is orderID-only
                };
            }

            console.warn('❌ [parseScanData] No match found for scan string:', scanString);
            return null;
        },

        // Tracking ID Detection and Handling
        isTrackingIdScan(scanString) {
            if (!scanString) return false;

            // Article scans always contain pipes (delimiter) after normalization
            // Example: MM26856|latte_safari_afrika|A1|5785421949909196
            if (scanString.includes("|")) {
                return false; // This is an article scan
            }

            // Check if this is an orderID-only scan (transition period support)
            // OrderIDs can be: MM12345, #MM12345, 465446, MM123456789, etc.
            // Pattern: Optional # + (1-3 letters + 4-12 digits OR 5-12 pure digits)
            // IMPORTANT: Must end with digits to avoid matching tracking IDs like LF629121868DE
            const orderIdPattern = /^#?(?:[A-Z]{1,3}\d{4,12}|\d{5,12})$/i;
            const endsWithLetters = /[A-Z]{2,}$/i.test(scanString); // Check if ends with 2+ letters

            if (orderIdPattern.test(scanString) && !endsWithLetters) {
                console.log('📋 Detected as orderID scan, not tracking ID:', scanString);
                return false; // This is an orderID scan, not a tracking ID
            }

            // Check if it's alphanumeric (letters and numbers only, no special chars)
            const isAlphanumeric = /^[A-Z0-9]+$/i.test(scanString);

            // Minimum length: 10 characters to support various tracking ID formats
            // Examples:
            // - DHL: 00340435068500145361 (20 chars)
            // - Hermes: CI836610632DE (13 chars)
            // - Other carriers may have different lengths
            const minLength = 10;
            const result = isAlphanumeric && scanString.length >= minLength;

            console.log(`🔍 Tracking ID detection for "${scanString}":`, {
                length: scanString.length,
                hasPipes: scanString.includes("|"),
                isAlphanumeric: isAlphanumeric,
                minLength: minLength,
                result: result
            });

            return result;
        },

        async handleTrackingIdScan(trackingId) {
            console.log('📋 Processing tracking ID scan:', trackingId);
            console.log('📋 Scan count:', this.scannedTrackingIds.length, '/', this.requiredTrackingScans);

            // Mobile workflow: Check if we're in the right step (7 for label scan, 8 for additional tracking)
            if (this.isMobile && this.mobileWorkflowStep !== 7 && this.mobileWorkflowStep !== 8) {
                this.showSnackbar('❌ Jetzt keine Tracking-ID scannen', 'warning');
                return;
            }

            // Swiss workflow: Handle two-scan process (works on both desktop and mobile)
            if (this.isSwissOrder) {
                if (!this.swissDocumentsPrinted) {
                    this.showSnackbar('❌ Bitte zuerst A4 Dokumente drucken', 'warning');
                    return;
                }

                // First scan (A4 documents) - triggers shipping label print
                if (!this.swissTrackingScan) {
                    console.log('📋 Swiss workflow: First tracking scan from A4 document');

                    // Validate tracking ID matches expected value
                    if (this.tracking && trackingId !== this.tracking) {
                        this.showSnackbar(`❌ Falsche Tracking ID. Erwartet: ${this.tracking.substring(0, 8)}...`, 'error');
                        return;
                    }

                    this.swissTrackingScan = true;
                    this.scannedTrackingIds.push(trackingId);  // Track first scan
                    this.showSnackbar('✅ A4 Tracking gescannt - Label wird gedruckt', 'success');

                    // Print shipping label + Prime label if applicable
                    await this.printShippingLabel();
                    return;
                }

                // Second scan (shipping label) - continues to normal validation
                console.log('📋 Swiss workflow: Second tracking scan from shipping label');
            }

            // Check if we're in the right state for tracking ID scanning
            if (!this.isPrinted) {
                this.showSnackbar('❌ Bitte zuerst Label drucken', 'warning');
                return;
            }

            // Simple scan counter logic
            // Check if we've already scanned enough times
            if (this.scannedTrackingIds.length >= this.requiredTrackingScans) {
                this.showSnackbar(`📋 Tracking ID bereits gescannt`, 'info');
                return;
            }

            // Validate tracking ID matches expected value
            if (this.tracking && trackingId !== this.tracking) {
                this.showSnackbar(`❌ Falsche Tracking ID. Erwartet: ${this.tracking.substring(0, 8)}...`, 'error');
                return;
            }

            // Add scan
            this.scannedTrackingIds.push(trackingId);
            this.scannedTrackingId = trackingId;

            const scansRemaining = this.requiredTrackingScans - this.scannedTrackingIds.length;

            if (scansRemaining > 0) {
                this.showSnackbar(`✅ Tracking ID gescannt (noch ${scansRemaining}x scannen)`, 'success');
            } else {
                this.trackingIdScanned = true;
                this.showSnackbar(`✅ Alle Tracking IDs gescannt - Verpacke...`, 'success');

                // Auto-pack the order
                console.log('🎁 Auto-packing order after final tracking scan');
                await this.pack();
            }
        },

        async handleMultipleTrackingIdScan(trackingId) {
            console.log('📋 Processing multiple tracking ID scan:', trackingId);
            console.log('📋 Required tracking IDs:', this.requiredTrackingIds);
            console.log('📋 Already scanned:', this.scannedTrackingIds);

            // Check if this tracking ID is in the required list
            if (!this.requiredTrackingIds.includes(trackingId)) {
                this.showSnackbar(`❌ Unbekannte Tracking ID: ${trackingId.substring(0, 8)}...`, 'error');
                console.log(`❌ Tracking ID not in required list: ${trackingId}`);
                return;
            }

            // For Swiss orders, the same tracking ID needs to be scanned twice
            // Count how many times this ID appears in requiredTrackingIds
            const requiredCount = this.requiredTrackingIds.filter(id => id === trackingId).length;
            const scannedCount = this.scannedTrackingIds.filter(id => id === trackingId).length;

            // Check if already scanned the required number of times
            if (scannedCount >= requiredCount) {
                this.showSnackbar(`📋 Tracking ID bereits gescannt: ${trackingId.substring(0, 8)}...`, 'info');
                return;
            }

            // Add to scanned list (allows duplicates for Swiss orders)
            this.scannedTrackingIds.push(trackingId);
            this.scannedTrackingId = trackingId; // Keep for backward compatibility

            const progress = this.trackingIdsProgress;
            this.showSnackbar(
                `✅ Tracking ID gescannt: ${trackingId.substring(0, 8)}... (${progress.scanned}/${progress.total})`,
                'success'
            );
            console.log(`✅ Tracking ID scanned and stored: ${trackingId}`);
            console.log(`📊 Progress: ${progress.scanned}/${progress.total} tracking IDs scanned`);

            // Check if all tracking IDs are now scanned
            if (this.allTrackingIdsScanned) {
                console.log('✅ All tracking IDs scanned! Ready to pack.');
                this.showSnackbar('✅ Alle Tracking IDs gescannt - bereit zum Verpacken', 'success');

                // Set legacy flag for compatibility
                this.trackingIdScanned = true;

                // Automatically pack and proceed to next order
                await this.pack();
            } else {
                const remaining = this.remainingTrackingIds;
                console.log(`⏳ Still need to scan ${remaining.length} tracking IDs:`, remaining.map(id => id.substring(0, 8) + '...'));
                this.showSnackbar(
                    `⏳ Noch ${remaining.length} Tracking ID${remaining.length > 1 ? 's' : ''} scannen`,
                    'info'
                );
            }
        },

        async handleSingleTrackingIdScan(trackingId) {
            console.log('📋 Processing single tracking ID scan:', trackingId);

            if (this.trackingIdScanned) {
                this.showSnackbar('📋 Tracking ID bereits gescannt', 'info');
                return;
            }

            this.scannedTrackingId = trackingId;
            this.scannedTrackingIds = [trackingId]; // Store in array for consistency
            this.trackingIdScanned = true;

            this.showSnackbar(`✅ Tracking ID gescannt: ${trackingId.substring(0, 8)}...`, 'success');
            console.log(`✅ Tracking ID scanned and stored: ${trackingId}`);

            // Automatically pack and proceed to next order
            await this.pack();
        },

        async handleScannedData(scannedData) {
            console.log('📦 Handling scanned data:', scannedData);
            console.log('📦 Current order ID:', this.currentOrder?.orderId);
            console.log('📦 Available products count:', this.currentProducts?.length);

            let matchingArticle = null;

            // TRANSITION PERIOD: Handle orderID-only scans
            if (scannedData.orderIdOnly) {
                console.log('📋 [OrderID-Only] Scan detected - finding next unscanned article');
                console.log('📋 [OrderID-Only] Mobile workflow:', {
                    isMobile: this.isMobile,
                    step: this.mobileWorkflowStep,
                    pickingScans: Object.keys(this.pickingScannedArticles).length,
                    packingScans: Object.keys(this.packingScannedArticles).length
                });

                // Verify the orderID matches the current order
                const currentOrderId = String(this.currentOrder?.orderId || '').replace('#', '');
                const scannedOrderId = String(scannedData.orderId || '').replace('#', '');

                console.log('📋 [OrderID-Only] Comparing:', {
                    current: currentOrderId,
                    scanned: scannedOrderId,
                    match: currentOrderId === scannedOrderId
                });

                if (currentOrderId !== scannedOrderId) {
                    console.error('❌ [OrderID-Only] Scanned orderID does not match current order');
                    this.showScanError('wrong-article', {
                        scannedSku: `Order ${scannedData.orderId}`,
                        expectedSku: `Order ${this.currentOrder?.orderId}`
                    });
                    return;
                }

                // Find the next article that hasn't been fully scanned yet
                // This allows multiple scans of the same orderID to progress through articles
                // IMPORTANT: Check scannedCount from currentOrder.products (desktop) or pickingScannedArticles (mobile)
                // because currentProducts is a computed array with new object references
                matchingArticle = this.currentProducts.find(product => {
                    // Find the actual product in currentOrder.products to get the real scannedCount
                    const actualProduct = this.currentOrder.products.find(p =>
                        p.sku === product.sku &&
                        (p.shelfHashcode || p.hashcode) === (product.shelfHashcode || product.hashcode)
                    );

                    let scannedCount = 0;
                    const requiredQty = product.qty || 1;

                    // Mobile: Check pickingScannedArticles or packingScannedArticles
                    if (this.isMobile) {
                        const articleKey = `${product.sku}_${product.shelfHashcode || product.hashcode}`;
                        if (this.mobileWorkflowStep === 1) {
                            scannedCount = this.pickingScannedArticles[articleKey] || 0;
                        } else if (this.mobileWorkflowStep === 3) {
                            scannedCount = this.packingScannedArticles[articleKey] || 0;
                        }
                    } else {
                        // Desktop/Tablet: Check scannedCount on product
                        scannedCount = actualProduct?.scannedCount || 0;
                    }

                    const needsMoreScans = scannedCount < requiredQty;

                    console.log('🔍 Checking article for orderID scan:', {
                        sku: product.sku,
                        scannedCount,
                        requiredQty,
                        needsMoreScans,
                        isMobile: this.isMobile,
                        mobileStep: this.mobileWorkflowStep
                    });

                    return needsMoreScans;
                });

                if (!matchingArticle) {
                    console.log('✅ All articles already scanned for this order');
                    this.showSnackbar('✅ Alle Artikel bereits gescannt', 'success');
                    return;
                }

                console.log('📦 Found next unscanned article:', {
                    sku: matchingArticle.sku,
                    scannedCount: matchingArticle.scannedCount || 0,
                    qty: matchingArticle.qty || 1
                });

                // CRITICAL: Replace matchingArticle with the actual product from currentOrder.products
                // because matchingArticle is from currentProducts (computed/cached) with stale scannedCount
                const matchHashcode = matchingArticle.shelfHashcode || matchingArticle.hashcode;
                const actualProduct = this.currentOrder.products.find(p => {
                    if (p.sku !== matchingArticle.sku) return false;

                    // If we have a hashcode, match by it to distinguish duplicate SKUs
                    if (matchHashcode) {
                        return (p.shelfHashcode || p.hashcode) === matchHashcode;
                    }

                    // No hashcode - for orderID scans, match by SKU and ensure not fully scanned
                    const pScannedCount = p.scannedCount || 0;
                    const pRequiredQty = p.qty || 1;
                    return pScannedCount < pRequiredQty;
                });

                if (actualProduct) {
                    matchingArticle = actualProduct;
                    console.log('📦 Using actual product from currentOrder.products:', {
                        sku: actualProduct.sku,
                        hashcode: actualProduct.shelfHashcode || actualProduct.hashcode,
                        scannedCount: actualProduct.scannedCount || 0
                    });
                } else {
                    console.error('❌ Could not find actual product in currentOrder.products!');
                }
            } else {
                // FULL BARCODE SCAN: Match by SKU, orderId, and hashcode
                matchingArticle = this.currentProducts.find(product => {
                    // Match by SKU, orderId, and hashcode
                    const productOrderId = String(product.orderId || product.translated?.orderId || '');
                    const scannedOrderId = String(scannedData.orderId || '');

                    const skuMatch = product.sku === scannedData.sku;
                    const orderIdMatch = productOrderId === scannedOrderId ||
                        productOrderId.replace('#', '') === scannedOrderId.replace('#', '');

                    // Additional hashcode validation if available
                    let hashcodeMatch = true;
                    if (product.shelfHashcode && scannedData.hashcode) {
                        hashcodeMatch = String(product.shelfHashcode) === String(scannedData.hashcode);
                    }

                    console.log('🔍 Product matching check:', {
                        productSku: product.sku,
                        scannedSku: scannedData.sku,
                        productOrderId,
                        scannedOrderId,
                        productHashcode: product.shelfHashcode,
                        scannedHashcode: scannedData.hashcode,
                        skuMatch,
                        orderIdMatch,
                        hashcodeMatch,
                        finalMatch: skuMatch && orderIdMatch && hashcodeMatch
                    });

                    return skuMatch && orderIdMatch && hashcodeMatch;
                });
            }

            if (!matchingArticle) {
                console.error('❌ Scanned article not found in current order');
                console.error('🔍 Scanned data details:', {
                    orderId: scannedData.orderId,
                    sku: scannedData.sku,
                    location: scannedData.location,
                    hashcode: scannedData.hashcode
                });
                console.error('🔍 Available products:', this.currentProducts.map(p => ({
                    sku: p.sku,
                    orderId: p.orderId || p.translated?.orderId,
                    shelfHashcode: p.shelfHashcode
                })));

                this.showScanError('wrong-article', {
                    scannedSku: scannedData.sku,
                    expectedSku: this.currentProducts.map(p => p.sku).join(', ')
                });
                return;
            }

            console.log('✅ Found matching article:', {
                sku: matchingArticle.sku,
                scannedCount: matchingArticle.scannedCount || 0,
                qty: matchingArticle.qty || 1
            });

            // CRITICAL: Ensure we use the actual product from currentOrder.products
            // For full barcode scans, also replace with actual product to avoid stale data
            if (!scannedData.orderIdOnly) {
                const matchHashcode = matchingArticle.shelfHashcode || matchingArticle.hashcode;
                const actualProduct = this.currentOrder.products.find(p => {
                    if (p.sku !== matchingArticle.sku) return false;

                    // Match by hashcode to distinguish duplicate SKUs
                    if (matchHashcode) {
                        return (p.shelfHashcode || p.hashcode) === matchHashcode;
                    }

                    // Fallback: just match by SKU
                    return true;
                });

                if (actualProduct) {
                    matchingArticle = actualProduct;
                    console.log('📦 Using actual product from currentOrder.products for full barcode scan:', {
                        sku: actualProduct.sku,
                        hashcode: actualProduct.shelfHashcode || actualProduct.hashcode,
                        scannedCount: actualProduct.scannedCount || 0
                    });
                }
            }

            // Process the scan for this article
            await this.scanArticle(matchingArticle, scannedData);
        },

        async scanArticle(article, scannedData = null) {
            // Block article scanning if order is splittable (no carton found)
            // User must complete split before scanning articles
            if (this.isSplittable) {
                this.showSnackbar('❌ Bitte erst Paket aufteilen', 'warning');
                this.openSplitModal();
                return;
            }

            // Mobile workflow: Track scans separately for picking vs packing phases
            if (this.isMobile) {
                const articleKey = `${article.sku}_${article.shelfHashcode}`;

                if (this.mobileWorkflowStep === 1) {
                    // Picking phase
                    const currentCount = this.pickingScannedArticles[articleKey] || 0;
                    const requiredQty = article.qty || 1;

                    if (currentCount >= requiredQty) {
                        this.showSnackbar(`✅ ${article.sku} bereits gescannt`, 'info');
                        return;
                    }

                    this.$set(this.pickingScannedArticles, articleKey, currentCount + 1);
                    console.log(`[Mobile Picking] Scanned ${article.sku}: ${currentCount + 1}/${requiredQty}`);

                    // Emit socket event for real-time sync to other devices
                    this.emitScanUpdate(article.id || article._id || article.sku, {
                        scannedCount: currentCount + 1,
                        totalQuantity: requiredQty,
                        isFullyScanned: (currentCount + 1) >= requiredQty,
                        scannedData: scannedData,
                        phase: 'picking'
                    });

                    // Emit mobile workflow update immediately after scan
                    console.log('[Mobile Picking] Emitting workflow update after scan');
                    this.emitMobileWorkflowUpdate();

                    if (currentCount + 1 >= requiredQty) {
                        this.showSnackbar(`✅ ${article.sku} vollständig gescannt`, 'success');
                    } else {
                        this.showSnackbar(`📦 ${article.sku} (${currentCount + 1}/${requiredQty})`, 'info');
                    }
                    return;

                } else if (this.mobileWorkflowStep === 3) {
                    // Packing verification phase
                    const currentCount = this.packingScannedArticles[articleKey] || 0;
                    const requiredQty = article.qty || 1;

                    if (currentCount >= requiredQty) {
                        this.showSnackbar(`✅ ${article.sku} bereits verifiziert`, 'info');
                        return;
                    }

                    this.$set(this.packingScannedArticles, articleKey, currentCount + 1);
                    console.log(`[Mobile Packing] Verified ${article.sku}: ${currentCount + 1}/${requiredQty}`);

                    // Emit socket event for real-time sync to other devices
                    this.emitScanUpdate(article.id || article._id || article.sku, {
                        scannedCount: currentCount + 1,
                        totalQuantity: requiredQty,
                        isFullyScanned: (currentCount + 1) >= requiredQty,
                        scannedData: scannedData,
                        phase: 'packing'
                    });

                    // Emit mobile workflow update immediately after scan
                    console.log('[Mobile Packing] Emitting workflow update after scan');
                    this.emitMobileWorkflowUpdate();

                    if (currentCount + 1 >= requiredQty) {
                        this.showSnackbar(`✅ ${article.sku} verifiziert`, 'success');
                    } else {
                        this.showSnackbar(`📦 ${article.sku} (${currentCount + 1}/${requiredQty})`, 'info');
                    }

                    // Check if packing is complete and auto-advance
                    if (this.mobilePackingComplete) {
                        setTimeout(() => {
                            this.advanceMobileWorkflowStep();
                        }, 500);
                    }
                    return;
                } else {
                    // Wrong step for scanning articles
                    this.showSnackbar('❌ Jetzt keine Artikel scannen', 'warning');
                    return;
                }
            }

            // Desktop/Tablet mode: Use original logic
            const currentScannedCount = article.scannedCount || 0;
            const requiredQty = article.qty || 1;

            // Check if article is already fully scanned
            if (currentScannedCount >= requiredQty) {
                this.showScanError('quantity-exceeded', {
                    itemSku: article.sku,
                    scannedCount: currentScannedCount,
                    requiredQty: requiredQty
                });
                return;
            }

            // Pack tracking is now handled automatically by EventBus system

            // Find and update the article in the original currentOrder.products array
            const originalProductIndex = this.currentOrder.products.findIndex(p =>
                p.designId === article.designId && p.sku === article.sku
            );

            if (originalProductIndex !== -1) {
                const newScannedCount = currentScannedCount + 1;

                // Update scanned count
                this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', newScannedCount);
                this.$set(this.currentOrder.products[originalProductIndex], 'lastScanTime', new Date().toISOString());

                // Mark as fully scanned if all quantity is scanned
                if (newScannedCount >= requiredQty) {
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', true);
                    console.log(`✅ Article ${article.sku} fully scanned (${newScannedCount}/${requiredQty})`);
                    this.showSnackbar(`✅ ${article.sku} vollständig gescannt`, 'success');
                } else {
                    console.log(`⏳ Article ${article.sku} partially scanned (${newScannedCount}/${requiredQty})`);
                    this.showSnackbar(`📦 ${article.sku} gescannt (${newScannedCount}/${requiredQty})`, 'info');
                }

                console.log(`✅ Real scan processed: ${article.sku} (${newScannedCount}/${requiredQty})`);

                // Emit socket event for real-time sync
                this.emitScanUpdate(article.id || article._id || article.sku, {
                    scannedCount: newScannedCount,
                    totalQuantity: requiredQty,
                    isFullyScanned: newScannedCount >= requiredQty,
                    scannedData: scannedData
                });

                // Check if all items are scanned
                if (this.scannedItems >= this.itemsToScan) {
                    // Don't auto-print if order is splittable (no carton found)
                    // User must complete split first
                    if (!this.isSplittable) {
                        await this.print();
                    } else {
                        console.log('📦 All items scanned but order is splittable - waiting for split completion');
                        this.showSnackbar('✅ Alle Artikel gescannt - Bitte Paket aufteilen', 'warning');
                    }
                }
            }
        },


        resetScanning() {
            // Reset scanned state on all products in currentOrder
            if (this.currentOrder && this.currentOrder.products) {
                this.currentOrder.products.forEach((product, index) => {
                    this.$set(this.currentOrder.products[index], 'scanned', false);
                    this.$set(this.currentOrder.products[index], 'scannedCount', 0);
                    this.$delete(this.currentOrder.products[index], 'scanTime');
                    this.$delete(this.currentOrder.products[index], 'lastScanTime');
                });
            }

            this.currentScanIndex = 0;
            this.isScanning = false;
            console.log('🔄 Scanning reset - all products marked as unscanned');
        },

        // Reset scan progress and notify other clients
        resetScanProgress() {
            console.log('🔄 Manual scan progress reset requested');

            // Reset local state
            this.resetScanning();

            // Reset print status as well
            this.isPrinted = false;
            this.isPrinting = false;
            this.trackingIdScanned = false;
            this.scannedTrackingId = null;
            this.scannedTrackingIds = [];
            this.requiredTrackingScans = 1;  // Reset to default

            // Emit reset event to clear server state and notify other clients
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/emitOrderStatusChange', {
                    orderId: this.currentOrder.orderId,
                    status: 'scan-reset'
                });

                // Clear scan progress for all items
                this.currentOrder.products.forEach(product => {
                    this.emitItemReset(product.id || product._id || product.sku);
                });
            }

            this.showSnackbar('🔄 Scan-Fortschritt zurückgesetzt', 'info');
        },

        // Apply scan progress from store to local order (for persistence across reloads)
        applyScanProgressFromStore() {
            const scanProgress = this.$store.state.orderSync.scanProgress;
            if (!scanProgress || !this.currentOrder?.products) return;

            let restoredCount = 0;
            Object.keys(scanProgress).forEach(itemId => {
                const progress = scanProgress[itemId];

                // Find the product in current order (try multiple matching strategies)
                let productIndex = this.currentOrder.products.findIndex(p =>
                    (p.id || p._id || p.sku) === itemId
                );

                // If not found, try matching by SKU only
                if (productIndex === -1) {
                    productIndex = this.currentOrder.products.findIndex(p => p.sku === itemId);
                }

                if (productIndex !== -1) {
                    this.$set(this.currentOrder.products[productIndex], 'scannedCount', progress.scannedCount || 0);
                    this.$set(this.currentOrder.products[productIndex], 'scanned', progress.scanned || false);
                    if (progress.lastScanTime) {
                        this.$set(this.currentOrder.products[productIndex], 'lastScanTime', progress.lastScanTime);
                    }
                    restoredCount++;
                }
            });

            if (restoredCount > 0) {
                console.log(`[MulticartWizard] Restored scan progress for ${restoredCount} items`);
            }
        },
        split() {
            if (this.isPrinting) return;

            // Open split modal instead of immediate action
            this.isSplitModalOpen = true;
            console.log('📦 Opening package split modal...');
        },

        // Modal handlers
        async handleSplitComplete(splitData) {
            try {
                this.isSplitting = true;
                this.isPrinting = true;

                // Call the existing split package API
                const response = await PacklistService.splitPackage({
                    packages: splitData,
                    orderId: this.currentOrder.orderId,
                });

                if (response.data.success) {
                    this.showSnackbar('✅ Paket erfolgreich aufgeteilt', 'success');
                    this.isPrinted = true;
                    this.closeSplitModal();
                } else {
                    this.showSnackbar('❌ Fehler beim Aufteilen des Pakets', 'error');
                }
            } catch (error) {
                console.error('Error splitting package:', error);
                this.showSnackbar('❌ Fehler beim Aufteilen des Pakets', 'error');
            } finally {
                this.isSplitting = false;
                this.isPrinting = false;
            }
        },

        // Scan Error Modal handlers
        showScanError(errorType, errorContext = {}) {
            this.scanErrorModal = {
                show: true,
                errorType: errorType,
                errorContext: errorContext
            };
            console.log('🚨 Scan error triggered:', errorType, errorContext);
        },

        closeScanErrorModal() {
            this.scanErrorModal.show = false;
        },

        handleContinueScanning() {
            console.log('▶️ Continue scanning after error');
            // Skip to next unscanned item
            const unscannedArticles = this.currentProducts.filter(product => !product.scanned);
            if (unscannedArticles.length > 0) {
                console.log('🔄 Continuing to next unscanned item');
            }
            this.closeScanErrorModal();
        },

        // Mobile error message helpers
        getMobileErrorTitle(errorType) {
            const titles = {
                'wrong-article': 'Falscher Artikel',
                'quantity-exceeded': 'Zu viele gescannt',
                'already-scanned': 'Bereits gescannt',
                'not-in-order': 'Nicht in Bestellung',
                'scan-timeout': 'Scan-Timeout'
            };
            return titles[errorType] || 'Scan Fehler';
        },

        getMobileErrorMessage(errorType, errorContext = {}) {
            const messages = {
                'wrong-article': `Artikel ${errorContext.scannedSku || 'unbekannt'} gehört nicht zu dieser Bestellung.`,
                'quantity-exceeded': `Artikel ${errorContext.sku || 'unbekannt'} wurde zu oft gescannt. Max: ${errorContext.maxQty || 0}`,
                'already-scanned': `Dieser Artikel wurde bereits vollständig gescannt.`,
                'not-in-order': `Der gescannte Artikel gehört nicht zu Bestellung ${errorContext.orderId || 'unbekannt'}.`,
                'scan-timeout': 'Scan-Vorgang wurde unterbrochen.'
            };
            return messages[errorType] || 'Ein unerwarteter Fehler ist aufgetreten.';
        },

        handleResetCurrentItem() {
            console.log('🔄 Reset current item after error');
            if (this.currentScanIndex > 0) {
                this.currentScanIndex--;
                // Reset the previously scanned item
                const prevArticle = this.currentProducts[this.currentScanIndex];
                if (prevArticle) {
                    this.$set(prevArticle, 'scanned', false);
                    this.$delete(prevArticle, 'scanTime');
                }
            }
        },

        handleReportScanProblem(problemData) {
            console.log('🐛 Scan problem reported:', problemData);
            // Here you could send to your error reporting system
        },

        selectPrinter(printer) {
            console.log('🖨️ Printer selected in MulticartWizard:', printer);
            this.currentPrinter = printer;
            localStorage.setItem('currentPrinter', JSON.stringify(printer));
            this.isSettingsModalOpen = false; // Close settings modal
        },

        handleLayoutChanged(newLayout) {
            console.log('📐 Layout changed in MulticartWizard:', newLayout);
            this.articleLayout = newLayout;
        },

        openSplitModal() {
            this.isSplitModalOpen = true;
            console.log('📦 Split modal opened');
        },

        closeSplitModal() {
            this.isSplitModalOpen = false;
            console.log('📦 Split modal closed');
        },

        // Printer selection now handled by SettingsModal
        openProblemModal() {
            this.isProblemModalOpen = true;
        },
        openSettingsModal() {
            this.isSettingsModalOpen = true;
        },

        closeProblemModal() {
            this.isProblemModalOpen = false;
        },
        closeSettingsModal() {
            this.isSettingsModalOpen = false;
        },

        openWarrantyModal() {
            this.isWarrantyModalOpen = true;
        },

        closeWarrantyModal() {
            this.isWarrantyModalOpen = false;
        },

        async reportProblemAndRefresh(note) {
            if (!note) return;

            try {
                await Promise.all([
                    PacklistService.sendFeedback(this.currentOrder.orderId, note),
                    PacklistService.reportMultiCartOrder(this.currentOrder.orderId, true)
                ]);

                this.showSnackbar('✅ Problem gemeldet und Bestellung aktualisiert', 'success');
                console.log('✅ Problem reported and order refreshed successfully');

                this.closeProblemModal();

                // Reload the current order after problem report
                await this.getNextOrder();
            } catch (error) {
                console.error('❌ Error reporting problem and refreshing order:', error);
                this.showSnackbar('❌ Fehler beim Melden des Problems und Aktualisieren der Bestellung', 'error');
            }
        },

        async handleRefreshOrder() {
            try {
                // Reload the current order after problem report
                await this.getNextOrder();
                console.log('✅ Order refreshed after problem report');
            } catch (error) {
                console.error('❌ Error refreshing order:', error);
            }
        },

        copyToClipboard(text) {
            try {
                navigator.clipboard.writeText(text);
                this.showSnackbar(`✅ ${text} copied to clipboard`, 'success');
            } catch (error) {
                console.error('Clipboard error:', error);
                this.showSnackbar('❌ Failed to copy to clipboard', 'error');
            }
        },

        setDevmode() {
            this.isDevelopmentMode = !this.isDevelopmentMode;
        },

        // Damaged Dialog handlers
        closeDamagedDialog() {
            this.isDamagedDialogOpen = false;
            this.selectedArticleForReset = null;
            this.damageResetOnly = true;
        },

        async handleArticleReset(resetData) {
            try {
                console.log('Handling article reset:', resetData);

                // Check if the API call was successful
                if (resetData.success) {
                    const selectedArticles = resetData.selectedArticles || [];
                    const totalItems = resetData.totalItems || selectedArticles.length;
                    const resetOnly = resetData.resetOnly;

                    // Show success message based on number of articles
                    if (totalItems === 1) {
                        this.showSnackbar(`✅ Artikel ${selectedArticles[0]} erfolgreich ${resetOnly ? 'zurückgesetzt' : 'als fehlerhaft markiert'}`, 'success');
                        console.log(`✅ Article ${selectedArticles[0]} ${resetOnly ? 'reset' : 'marked as damaged'} successfully`);
                    } else {
                        this.showSnackbar(`✅ ${totalItems} Artikel erfolgreich ${resetOnly ? 'zurückgesetzt' : 'als fehlerhaft markiert'}`, 'success');
                        console.log(`✅ ${totalItems} articles ${resetOnly ? 'reset' : 'marked as damaged'} successfully`);
                    }

                    // Emit socket event for real-time sync for all articles
                    selectedArticles.forEach(sku => {
                        this.emitItemReset(sku);
                    });

                    // Close the dialog
                    this.closeDamagedDialog();

                    // Refresh the order to get updated data
                    await this.handleRefreshOrder();

                    console.log('🔄 Order refreshed after article reset');
                } else {
                    this.showSnackbar('❌ Artikel nicht gefunden', 'error');
                    console.error('❌ Article not found in current order for reset');
                }

            } catch (error) {
                console.error('❌ Error handling article reset:', error);
                this.showSnackbar('❌ Fehler beim Zurücksetzen des Artikels', 'error');
            }
        },

        // Snackbar helper
        showSnackbar(message, color = 'success', timeout = 3000) {
            this.snackbar = {
                show: true,
                message,
                color,
                timeout
            };
        },

        // Handle window resize for tablet detection
        handleResize() {
            this.windowWidth = window.innerWidth;
        },

        // Fullscreen management
        checkFullscreenSupport() {
            this.fullscreenSupported = !!(
                document.fullscreenEnabled ||
                document.webkitFullscreenEnabled ||
                document.mozFullScreenEnabled ||
                document.msFullscreenEnabled
            );
        },

        async enterFullscreen() {
            if (!this.fullscreenSupported) {
                console.warn('[Tablet] Fullscreen not supported');
                return;
            }

            try {
                const element = document.documentElement;

                if (element.requestFullscreen) {
                    await element.requestFullscreen();
                } else if (element.webkitRequestFullscreen) {
                    await element.webkitRequestFullscreen();
                } else if (element.mozRequestFullScreen) {
                    await element.mozRequestFullScreen();
                } else if (element.msRequestFullscreen) {
                    await element.msRequestFullscreen();
                }

                console.log('[Tablet] Entered fullscreen mode');
            } catch (error) {
                console.error('[Tablet] Error entering fullscreen:', error);
            }
        },

        async exitFullscreen() {
            try {
                if (document.exitFullscreen) {
                    await document.exitFullscreen();
                } else if (document.webkitExitFullscreen) {
                    await document.webkitExitFullscreen();
                } else if (document.mozCancelFullScreen) {
                    await document.mozCancelFullScreen();
                } else if (document.msExitFullscreen) {
                    await document.msExitFullscreen();
                }

                console.log('[Tablet] Exited fullscreen mode');
            } catch (error) {
                console.error('[Tablet] Error exiting fullscreen:', error);
            }
        },

        handleFullscreenChange() {
            const isFullscreen = !!(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );

            this.isFullscreen = isFullscreen;
            console.log('[Tablet] Fullscreen state changed:', isFullscreen);

            // If we exit fullscreen and we're in tablet mode, try to re-enter
            if (!isFullscreen && this.isTablet && this.$route.query.tablet) {
                setTimeout(() => {
                    this.enterFullscreen();
                }, 1000); // Small delay to avoid conflicts
            }
        },

        // Auto-enter fullscreen for tablet mode (simplified - now called on initial load)
        async handleTabletFullscreen() {
            if (this.isTablet && this.$route.query.tablet && this.fullscreenSupported && !this.isFullscreen) {
                console.log('[Tablet] Auto-entering fullscreen for tablet mode');
                await this.enterFullscreen();
            }
        },

        // Add meta viewport for better tablet experience
        setupTabletViewport() {
            // Check if we already have a viewport meta tag
            let viewportMeta = document.querySelector('meta[name="viewport"]');

            if (this.isTablet) {
                const tabletViewport = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';

                if (viewportMeta) {
                    viewportMeta.setAttribute('content', tabletViewport);
                } else {
                    viewportMeta = document.createElement('meta');
                    viewportMeta.name = 'viewport';
                    viewportMeta.content = tabletViewport;
                    document.head.appendChild(viewportMeta);
                }
                console.log('[Tablet] Viewport optimized for tablet');
            }
        },

        // Initialize order synchronization socket
        initOrderSync() {
            console.log('[MulticartWizard] initOrderSync called', {
                hasCurrentUser: !!this.currentUser,
                currentUser: this.currentUser
            });

            if (this.currentUser) {
                console.log('[MulticartWizard] ✅ Dispatching orderSync/initSocket');
                this.$store.dispatch('orderSync/initSocket', this.currentUser);
            } else {
                console.error('[MulticartWizard] ❌ Cannot init socket - no currentUser');
            }
        },

        // Join order room for real-time synchronization
        joinOrderRoom(orderId) {
            console.log('[MulticartWizard] joinOrderRoom called', {
                orderId,
                isConnected: this.orderSyncConnected,
                hasSocket: !!this.$store.state.orderSync.socket,
                currentRoom: this.currentOrderRoom
            });

            if (orderId) {
                this.$store.dispatch('orderSync/joinOrderRoom', orderId);
            } else {
                console.error('[MulticartWizard] ❌ Cannot join order room - no orderId provided');
            }
        },

        // Leave current order room
        leaveOrderRoom() {
            this.$store.dispatch('orderSync/leaveOrderRoom');
        },

        // Emit scan item event to other connected clients
        emitScanUpdate(itemId, scannedData) {
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/emitScanItem', {
                    orderId: this.currentOrder.orderId,
                    itemId: itemId,
                    scannedData: scannedData
                });
            }
        },

        // Emit item reset event to other connected clients
        emitItemReset(itemId) {
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/emitItemReset', {
                    orderId: this.currentOrder.orderId,
                    itemId: itemId
                });
            }
        },

        // Emit print status update
        emitPrintStatusUpdate() {
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/emitPrintStatusUpdate', {
                    orderId: this.currentOrder.orderId,
                    printStatus: {
                        isPrintable: this.isPrintable,
                        isPrinting: this.isPrinting,
                        isPrinted: this.isPrinted
                    }
                });
            }
        },

        // Emit pack status update
        emitPackStatusUpdate() {
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/emitPackStatusUpdate', {
                    orderId: this.currentOrder.orderId,
                    packingStatus: {
                        isPackable: this.isPrintable && this.isPrinted,
                        isPacked: false // Will be true when pack() is called
                    }
                });
            }
        },

        // Emit mobile workflow state update
        emitMobileWorkflowUpdate() {
            console.log('[emitMobileWorkflowUpdate] Called', {
                hasOrderId: !!this.currentOrder?.orderId,
                orderId: this.currentOrder?.orderId,
                isMobile: this.isMobile,
                isConnected: this.orderSyncConnected,
                currentRoom: this.currentOrderRoom,
                step: this.mobileWorkflowStep,
                pickingScans: Object.keys(this.pickingScannedArticles).length,
                packingScans: Object.keys(this.packingScannedArticles).length
            });

            if (this.currentOrder?.orderId && this.isMobile) {
                const workflowState = {
                    step: this.mobileWorkflowStep,
                    pickingScannedArticles: this.pickingScannedArticles,
                    packingScannedArticles: this.packingScannedArticles
                };

                console.log('[emitMobileWorkflowUpdate] ✅ Dispatching to store', workflowState);

                this.$store.dispatch('orderSync/emitMobileWorkflowUpdate', {
                    orderId: this.currentOrder.orderId,
                    workflowState
                });
            } else {
                console.log('[emitMobileWorkflowUpdate] ❌ Not dispatching - conditions not met');
            }
        },

        // Request order state from server
        requestOrderState() {
            if (this.currentOrder?.orderId) {
                this.$store.dispatch('orderSync/requestOrderState', this.currentOrder.orderId);
            }
        },

        // Dev Mode Methods
        async devToggleArticleScanned(article) {
            if (!this.isDevelopmentMode) return;

            const currentScannedCount = article.scannedCount || 0;
            const requiredQty = article.qty || 1;

            // Find the article in currentOrder.products
            const originalProductIndex = this.currentOrder.products.findIndex(p =>
                p.designId === article.designId && p.sku === article.sku
            );

            if (originalProductIndex !== -1) {
                if (currentScannedCount >= requiredQty) {
                    // Unscan it
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', 0);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', false);
                    console.log(`🧪 [DEV] Article ${article.sku} unscanned (0/${requiredQty})`);
                    this.showSnackbar(`🧪 ${article.sku} unscanned`, 'info');
                } else {
                    // Scan to full
                    this.$set(this.currentOrder.products[originalProductIndex], 'scannedCount', requiredQty);
                    this.$set(this.currentOrder.products[originalProductIndex], 'scanned', true);
                    this.$set(this.currentOrder.products[originalProductIndex], 'lastScanTime', new Date().toISOString());
                    console.log(`🧪 [DEV] Article ${article.sku} fully scanned (${requiredQty}/${requiredQty})`);
                    this.showSnackbar(`🧪 ${article.sku} scanned`, 'success');
                }

                // Emit socket event for sync
                this.emitScanUpdate(article.id || article._id || article.sku, {
                    scannedCount: this.currentOrder.products[originalProductIndex].scannedCount,
                    totalQuantity: requiredQty,
                    isFullyScanned: this.currentOrder.products[originalProductIndex].scannedCount >= requiredQty
                });

                // Check if all items are scanned and auto-print
                await this.$nextTick(); // Wait for reactive updates
                if (this.scannedItems >= this.itemsToScan && !this.isPrinted) {
                    console.log('🧪 [DEV] Auto-printing label...');
                    await this.print();
                }
            }
        },

        async devScanAllArticles() {
            if (!this.isDevelopmentMode || !this.currentOrder?.products) return;

            console.log('🧪 [DEV] Scanning all articles...');

            this.currentOrder.products.forEach((product, index) => {
                const requiredQty = product.qty || 1;
                this.$set(this.currentOrder.products[index], 'scannedCount', requiredQty);
                this.$set(this.currentOrder.products[index], 'scanned', true);
                this.$set(this.currentOrder.products[index], 'lastScanTime', new Date().toISOString());

                // Emit socket event for each item
                this.emitScanUpdate(product.id || product._id || product.sku, {
                    scannedCount: requiredQty,
                    totalQuantity: requiredQty,
                    isFullyScanned: true
                });
            });

            this.showSnackbar('🧪 All articles scanned', 'success');
            console.log('🧪 [DEV] All articles scanned');

            // Check if all items are scanned and auto-print
            if (this.scannedItems >= this.itemsToScan && !this.isPrinted) {
                console.log('🧪 [DEV] Auto-printing label...');
                await this.print();
            }
        },

        async devSimulateTrackingScan() {
            if (!this.isDevelopmentMode) return;

            console.log('🧪 [DEV] Simulating tracking ID scan...');

            if (this.hasAdditionalTrackingIds) {
                // Scan all remaining tracking IDs
                const remainingIds = this.remainingTrackingIds;
                for (const trackingId of remainingIds) {
                    await this.handleTrackingIdScan(trackingId);
                }
            } else if (this.currentOrder.tracking) {
                // Scan single tracking ID
                await this.handleTrackingIdScan(this.currentOrder.tracking);
            }
        }
    },
    async mounted() {
        // Read shelf parameter from route query (default to 'lower')
        this.currentShelf = this.$route.query.shelf || 'lower';
        console.log(`📦 Multicart Wizard mounted - Shelf: ${this.currentShelf}`);

        // Expose dev mode toggle to console
        window.toggleDevMode = () => {
            this.isDevelopmentMode = !this.isDevelopmentMode;
            console.log(`🔧 Dev Mode ${this.isDevelopmentMode ? 'ENABLED' : 'DISABLED'}`);
            return this.isDevelopmentMode;
        };

        console.log('💡 Console commands available:');
        console.log('   - window.toggleDevMode() : Toggle dev mode on/off');

        window.addEventListener('resize', this.handleResize);

        // Initialize fullscreen support
        this.checkFullscreenSupport();

        // Add fullscreen event listeners
        document.addEventListener('fullscreenchange', this.handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);

        // Immediately enter fullscreen if in tablet mode (before loading order)
        this.$nextTick(() => {
            this.setupTabletViewport();
            if (this.isTablet && this.$route.query.tablet) {
                console.log('[Tablet] Entering fullscreen on initial load');
                this.enterFullscreen();
            }

            // Check if printer is selected on mobile, if not open modal
            if (this.isMobile && !this.currentPrinter) {
                console.log('[Mobile] No printer selected, opening printer selection modal');
                this.isSettingsModalOpen = true;
            }
        });

        // Load pack stats in background (don't wait for it)
        this.loadPackStats().catch(error => {
            console.error('Error loading pack stats in background:', error);
        });

        // Initialize order synchronization socket
        this.initOrderSync();

        //Get next Order
        await this.getNextOrder();
    },
    beforeDestroy() {
        // Clean up Socket.io listeners
        if (this.messageSocket) {
            console.log('[MulticartWizard] Removing packlist-alert listener');
            this.messageSocket.off('packlist-alert', this.handlePacklistAlert);
        }

        // Clean up console commands
        delete window.toggleDevMode;

        window.removeEventListener('resize', this.handleResize);

        // Remove fullscreen event listeners
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
        document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);

        // Exit fullscreen if active
        if (this.isFullscreen) {
            this.exitFullscreen();
        }

        // Leave current order room before cleanup
        console.log('[MulticartWizard] beforeDestroy - leaving order room');
        this.$store.dispatch('orderSync/leaveOrderRoom');

        // Small delay to ensure leave event is processed
        setTimeout(() => {
            // Cleanup order sync socket
            this.$store.dispatch('orderSync/cleanup');
        }, 100);
    }
}
</script>

<style>
.multicart-wizard {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    box-sizing: border-box;
    overflow: hidden;
    /* Prevent whole page scroll */
}

.multicart-wizard-header-body-container {
    display: flex;
    flex-direction: row;
    flex: 1;
    /* Take remaining space between header and actions */
    min-height: 0;
    /* Important for flex shrinking */
    border-bottom: 1px solid #e0e0e0;
    overflow: hidden;
    /* Contain overflow within this container */
}

/* Development Mode Badge */
.dev-mode-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 1000;
}

/* Back Button - scoped to multicart-wizard only */
.multicart-wizard>.back-button {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1000;
}

/* Socket.IO debug info */
.socket-debug-info {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.socket-debug-info .v-chip {
    cursor: pointer;
    transition: all 0.2s ease;
}

.socket-debug-info .v-chip:hover {
    transform: scale(1.05);
}

/* Dev Terminal Styles */
.dev-terminal {
    position: fixed;
    top: 70px;
    right: 16px;
    width: 400px;
    max-height: calc(100dvh - 100px);
    z-index: 9999;
    background: #0d1117;
    border: 1px solid #00ff41;
    border-radius: 8px;
    box-shadow: 0 0 20px rgba(0, 255, 65, 0.3),
        0 0 40px rgba(0, 255, 65, 0.1);
    font-family: 'Courier New', monospace;
    overflow: hidden;
}

.terminal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: linear-gradient(180deg, #1a1f2e 0%, #0d1117 100%);
    border-bottom: 1px solid #00ff41;
}

.terminal-title {
    display: flex;
    align-items: center;
    color: #00ff41;
    font-size: 12px;
    font-weight: bold;
    letter-spacing: 1px;
}

.terminal-blink {
    animation: blink 1s infinite;
}

@keyframes blink {

    0%,
    50% {
        opacity: 1;
    }

    51%,
    100% {
        opacity: 0;
    }
}

.terminal-status {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #00ff41;
    font-size: 10px;
    letter-spacing: 1px;
}

.status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #00ff41;
    box-shadow: 0 0 10px #00ff41;
    animation: pulse 2s infinite;
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.4;
    }
}

.terminal-body {
    padding: 12px;
    max-height: calc(100dvh - 150px);
    overflow-y: auto;
    background: #0d1117;
}

.terminal-body::-webkit-scrollbar {
    width: 8px;
}

.terminal-body::-webkit-scrollbar-track {
    background: #1a1f2e;
}

.terminal-body::-webkit-scrollbar-thumb {
    background: #00ff41;
    border-radius: 4px;
}

.terminal-content {
    color: #00ff41;
}

.terminal-section {
    margin-bottom: 12px;
}

.terminal-prompt {
    color: #00ff41;
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 8px;
    text-shadow: 0 0 5px rgba(0, 255, 65, 0.5);
}

.terminal-hint {
    color: #4a9960;
    font-size: 10px;
    font-style: italic;
    margin-top: 4px;
    margin-bottom: 8px;
}

.terminal-data {
    background: #1a1f2e;
    border: 1px solid #2d3748;
    border-left: 3px solid #00ff41;
    padding: 8px;
    border-radius: 4px;
    font-size: 10px;
    color: #00ff41;
    max-height: 150px;
    overflow: auto;
    margin: 0;
    line-height: 1.4;
    text-shadow: 0 0 2px rgba(0, 255, 65, 0.3);
}

.terminal-article-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.terminal-article-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    background: #1a1f2e;
    border: 1px solid #2d3748;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.terminal-article-item:hover {
    background: #2d3748;
    border-color: #00ff41;
}

.terminal-article-item.scanned {
    border-color: #00ff41;
    background: rgba(0, 255, 65, 0.1);
}

.article-sku {
    color: #00ff41;
    font-size: 11px;
    flex: 1;
}

.article-qty {
    color: #64ffda;
    font-size: 10px;
    margin-right: 8px;
}

.terminal-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
}

.terminal-btn {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: bold;
    font-family: 'Courier New', monospace;
    letter-spacing: 0.5px;
    border: 1px solid;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
    text-transform: uppercase;
}

.terminal-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    filter: brightness(1.2);
}

.terminal-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.terminal-btn-primary {
    background: transparent;
    color: #00ff41;
    border-color: #00ff41;
    box-shadow: 0 0 10px rgba(0, 255, 65, 0.3);
}

.terminal-btn-primary:hover:not(:disabled) {
    background: rgba(0, 255, 65, 0.1);
    box-shadow: 0 0 15px rgba(0, 255, 65, 0.5);
}

.terminal-btn-secondary {
    background: transparent;
    color: #64ffda;
    border-color: #64ffda;
}

.terminal-btn-secondary:hover:not(:disabled) {
    background: rgba(100, 255, 218, 0.1);
}

.terminal-btn-success {
    background: transparent;
    color: #ffeb3b;
    border-color: #ffeb3b;
    box-shadow: 0 0 10px rgba(255, 235, 59, 0.3);
}

.terminal-btn-success:hover:not(:disabled) {
    background: rgba(255, 235, 59, 0.1);
    box-shadow: 0 0 15px rgba(255, 235, 59, 0.5);
}

/* Mobile Error Snackbar */
.mobile-error-snackbar {
    margin-top: 80px !important;
}

.mobile-error-snackbar .v-snack__wrapper {
    min-height: 100px;
    padding: 16px 20px;
}

.mobile-error-snackbar .v-snack__content {
    font-size: 16px;
    line-height: 1.5;
}

/* Mobile-specific adjustments */
@media (max-width: 480px) {
    .mobile-error-snackbar {
        margin-top: 60px !important;
    }

    .mobile-error-snackbar .v-snack__wrapper {
        min-height: 120px;
        padding: 20px;
        border-radius: 12px;
    }

    .mobile-error-snackbar .text-h6 {
        font-size: 1.25rem !important;
    }

    .mobile-error-snackbar .text-body-2 {
        font-size: 1rem !important;
    }
}
</style>